import { accountOnHoldCopy } from './service-pause.js';
import { i18n } from '../i18n.js';
import { Confirm } from './confirm-dialog.js';
import { LEGAL_VERSIONS } from '../legal-versions.js';
import { WithdrawalPanel } from './withdrawal-panel.js';
import { isImmediateUpgradeCharge } from './store-upgrade-confirm.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * Store UI Module
 * Handles Manga Pass purchasing, subscription management, and Stardust credits
 */

import { BootVeil } from './boot-veil.js';
import { Collection } from './collection.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryLifecycle } from './foundry-lifecycle.js';
import { FoundrySurface } from './foundry-surface.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { OrientationGuard } from './orientation-guard.js';
import { Stardust } from './stardust.js';
import { announceStardust, lastKnownStardust } from './stardust-chip.js';
import { peekMoonlightStatus, renderMoonlightChip } from './moonlight-chip.js';
import { closeMoonlightCard, onKeyboardFocus, openMoonlightCard } from './moonlight-card.js';
import { isPlusPress, pulsePlus } from './chip-plus.js';
import {
    DEFAULT_PASS_PERIOD,
    PASS_PERIODS,
    buildPassCards,
    buildPassPeriodSelector,
    moonlightPlusTarget,
    passCardAmountsMinor,
    refillPriceMinor,
    usd,
} from './store-pass-cards.js';
import { RegionAvailability } from '../region-availability.js';
import { pendingUpgradeInvoiceUrl, readUpgradeQuote, upgradePriceText } from './store-upgrade-quote.js';
import {
    displayTotalMinor,
    showsTaxInclusive,
    taxNoteText,
    usableDisplayTax,
} from './store-display-price.js';
let Store = {};

Store = (function () {
    let isOpen = false;
    let initialized = false;
    let closeTimer = null;
    let billingStatusOverride = null;
    let storeKeyHandler = null;
    let storeReturnFocus = null;
    let storeBackgroundState = null;
    // The fade store.js runs from where the Store stands: a Store reopened on
    // its way out turning around (reopenFade), or one closed before it
    // reached the exit's first step (earlyExitFade).
    let storeResumeFade = null;
    let balanceRequest = 0;
    let parentSurfaceOwner = null;
    let stardustPackState = { status: 'pending', packs: [] };
    /* WHAT THIS BUYER'S COUNTRY ADDS, as Stripe Tax answered it for the exact
       figures below (`/api/region/availability`). Null until that lands, and
       null forever in a country that pays none — both of which render the
       Store exactly as it did before tax-inclusive display existed. */
    let storeDisplayTax = null;
    let displayTaxAnswer = null;
    let displayTaxRequested = false;
    /* Left unset at module scope on purpose. Reading an imported binding
       there is what `topLevelGlobals.test.js` refuses: `store.js` has nine
       client imports, so a future cycle would hand it an uninitialised
       binding — and the failure would be a silently wrong billing period. */
    let passPeriod = null;
    let activeBillingInterval = null;
    let complimentaryStatus = null;
    /* The Moonlight the store's meter shows: the answer the store holds
       (`storeReads.moonlight`, which outlives a close, so a reopen starts at
       the value rather than an em-dash, and is dropped the moment another
       account is signed in), or — before this account's first answer — the
       lobby chip's value (`paintStoreMoonlight`). */
    let storeMoonlightStatus = null;
    /* True once the store holds an answer to the subscription read (warmed or
       this open's own). The refill button waits for it: shown from the
       Moonlight read alone, it flashed up for a gift holder and vanished when
       the subscription read landed. */
    let storeSubscriptionResolved = false;
    /* What the opener asked the store to lead with: the Moonlight chip's "+"
       ('refill') asks for MORE MOONLIGHT — the passes for an account without
       one, the tier above for a Storyteller holder, the Refill for a Manga
       holder (`moonlightPlusTarget`); a Stardust chip's "+" ('stardust')
       asks for the Stardust packs. Settled by `settlePendingSpotlight` once
       the reads that decide the target have landed. */
    let pendingSpotlight = null;
    /* The store's own Moonlight "+" also SAYS what a refill is (the card). */
    let pendingSpotlightExplain = false;
    /* The pass the account holds, from the subscription answer:
       'moonfall_storyteller' | 'moonfall_manga' | null. */
    let activePassTier = null;
    /* True once the store holds a Moonlight answer, or THIS open's Moonlight
       read has failed: the refill's visibility is decided by it, so a "+"
       waiting for the refill stops waiting when the read is in. */
    let storeMoonlightResolved = false;
    let storeBalancePlusesWired = false;
    const currentPassPeriod = () => passPeriod || DEFAULT_PASS_PERIOD;

    const SUBSCRIPTION_STATUS_TIMEOUT_MS = 15_000;
    const CHECKOUT_RETURN_STORAGE_KEY = 'moonfall:store-checkout-return:v1';
    const CHECKOUT_RETURN_TTL_MS = 24 * 60 * 60_000;
    const CHECKOUT_RETURN_FUTURE_TOLERANCE_MS = 60_000;
    const COLLECTION_SECTIONS = new Set([
        'cards',
        'deckbuilder',
        'cardbacks',
        'exchange',
        'packs',
    ]);
    const FOUNDRY_TABS = new Set(['moon-fuse', 'moon-dream']);
    const SURFACE_OWNER = 'store';
    const LAYERABLE_SURFACE_SELECTORS = {
        collection: '#collection-modal',
        foundry: '[data-foundry-surface-root]',
    };
    /* The U+00AD in each fallback mirrors ui.json: a soft hyphen at the
       syllable boundary of "Stardust". It paints nothing unless the word must
       break — which it must on the 58-83px pack cards of the landscape squeeze
       band, where the only alternative was `overflow-wrap: anywhere` cutting
       it as "Stardus / t". `store.spec.js` measures every Latin title per
       character at each page box and accepts a break only at a hyphen. */
    const STARDUST_PACK_PRESENTATION = Object.freeze([
        Object.freeze({
            sku: 'stardust_100',
            titleKey: 'store.bagStardust',
            titleFallback: 'Bag of Star\u00ADdust',
            icon: 'stardust-bag.webp',
        }),
        Object.freeze({
            sku: 'stardust_250',
            titleKey: 'store.chestStardust',
            titleFallback: 'Chest of Star\u00ADdust',
            icon: 'stardust-chest.webp',
            // The middle pack carries the promotional rank. It is the price
            // point the ladder is built around, and an unranked ladder makes
            // the player price-compare three anonymous options instead of
            // being offered a recommendation.
            popular: true,
        }),
        Object.freeze({
            sku: 'stardust_500',
            titleKey: 'store.vaultStardust',
            titleFallback: 'Vault of Star\u00ADdust',
            icon: 'stardust-vault.webp',
        }),
    ]);

    const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

    /* THE ARTWORK SITS IN A WELL THE LAYOUT SIZES (docs/asset-loading.md law 2).
       A bare `<img>` has no box until its bytes land, so on every landscape
       phone the cards grew by the picture's height a moment after the store
       opened and the buy buttons moved under the player's thumb. The well is
       sized by CSS alone and the image is absolutely positioned inside it,
       so the space is the same before and after the swap. Not lazy: the
       elements are created at open, in view, and lazy loading only delayed
       the request that the well is now waiting for. */
    function buildPlanArt(icon, alt) {
        const hidden = alt ? '' : ' aria-hidden="true"';
        return `<div class="plan-art"><img class="plan-icon" src="${STORE_ART_ROOT}${icon}" alt="${alt}"${hidden} decoding="async"></div>`;
    }

    const STORE_ART_ROOT = 'assets/store/';

    /* THE EMBLEMS ARE WARMED BEFORE THE STORE IS OPENED (law 3), two ways.

       Each plan card prints its price, title and features at once and its
       emblem fills a well the layout sizes — text-first, as law 2 wants — but
       on a cold cache the five emblems (33-101KB each) were requested only as
       the cards rendered, so the modal faded in with empty boxes that filled
       a beat later; on a phone connection they were the thing the owner
       watched land.

       1. ON INTENT: hover, focus and the first touch on any `[data-open-store]`
          control — the same head start the Collection and Foundry buttons
          take — and a programmatic open. Delegated, so one listener each
          covers the lobby chip, the Collection's balance and the Foundry
          pills, like the click itself.
       2. FROM THE LOBBY, at idle after the page's own `load`: a phone has no
          hover to signal intent before the tap, and the lobby is the store's
          only doorway (`Store.warmArt`, called by main.js when the lobby
          paints). Never before `load` — an image request started while the
          document is still loading delays the load event until it lands, so
          a lobby restored from a saved session would have put five product
          pictures on the critical path of every `page.goto('/play')`.

       Both are best-effort, low priority and memoized: the first to run
       requests the five files once, and a failure changes nothing. Warm once,
       the open is a swap instead of a download.

       The warmed images are KEPT, and their decode is what the open waits on
       (capped, `STORE_OPEN_HOLD_MS`): a held image stays in the page's memory
       cache, so the store's own `<img>`s are complete the moment they are
       created, and the store is presented with its pictures rather than in
       front of them. The idle allowance after `load` is short (250 ms): the
       press can come a second after the lobby settles, and the old 4 s
       allowance let the store open with its pictures still downloading. */
    const STORE_ART = Object.freeze([
        ...STARDUST_PACK_PRESENTATION.map((pack) => pack.icon),
        'pass-storyteller.webp',
        'pass-manga.webp',
    ]);
    let storeArtWarmed = false;
    let storeArtScheduled = false;
    /* The five warm images, held so their decoded pixels stay available. */
    const storeArtImages = [];
    /* True once every warmed emblem has decoded or failed. A browser with no
       `decode()` gives nothing to wait on, so it counts as settled. */
    let storeArtSettled = false;

    /** `urgent` is the open's own warm: the store is waiting for these now. */
    function warmStoreArt(urgent) {
        if (storeArtWarmed) return;
        storeArtWarmed = true;
        const decodes = [];
        for (const file of STORE_ART) {
            try {
                const img = new Image();
                try { img.fetchPriority = urgent === true ? 'high' : 'low'; } catch (_) { /* not supported */ }
                img.decoding = 'async';
                img.src = `${STORE_ART_ROOT}${file}`;
                storeArtImages.push(img);
                if (typeof img.decode === 'function') decodes.push(img.decode().catch(() => {}));
            } catch (_) { /* a warm is a courtesy */ }
        }
        if (!decodes.length) {
            storeArtSettled = true;
            return;
        }
        Promise.all(decodes).then(() => {
            storeArtSettled = true;
            onStoreDataSettled();
        });
    }

    /* Every control that opens the store: the Stardust chips (`[data-open-store]`)
       and the lobby's Moonlight chip, which opens it on a desktop click. */
    const STORE_DOOR_SELECTOR = '[data-open-store], #user-moonlight';

    function handleStoreIntent(event) {
        const source = event.target;
        const trigger = source instanceof Element ? source.closest(STORE_DOOR_SELECTOR) : null;
        if (!trigger) return;
        warmStoreArt();
        warmStoreData();
    }

    /** The lobby's warm: after `load`, then at idle. An intent warm that
     *  lands first wins; this then finds nothing left to do. */
    function warmArt() {
        if (storeArtWarmed || storeArtScheduled || typeof Image !== 'function') return;
        storeArtScheduled = true;
        const whenIdle = () => {
            if (typeof window.requestIdleCallback === 'function') {
                window.requestIdleCallback(warmStoreArt, { timeout: 250 });
            } else {
                setTimeout(warmStoreArt, 250);
            }
        };
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });
    }

    /* THE STORE'S DATA IS WARMED THE SAME WAY (law 3), and the open paints
       from it.

       The store is three reads — the subscription (which pass cards and which
       buttons), the Stardust pack config (the packs and their prices) and the
       Moonlight status (the meter and the refill) — and they used to start at
       the press. The modal then faded in over two "Updating status..." cards,
       the packs arrived ~250 ms later and grew their section by 80 px, every
       title moved, the footer moved, and the meter's "—" became a figure. The
       owner's brief: screens "morph" while they load, loading is "chaotic",
       and too much is fetched "just in time instead of ahead of time".

       So the reads are taken ahead of the press — by the lobby as soon as the
       boot veil lifts (`Store.warmData`, from `Lobby.warmAtIdle` on sign-in
       and from the lobby's return from every match) and on intent (hover,
       focus, first touch on any store door) — and kept per account. The open
       paints every section from them in one pass before its first frame.

       AND ASKS AGAIN BEHIND IT, FOR THE ACCOUNT'S TWO ANSWERS (the read
       audit and the lead's decision on it, 26 September 2026). The pass and
       the Moonlight move without the store being told — a purchase on another
       device, a webhook that lands a minute after the checkout, a grant, a
       duel — so every open asks for both again, whatever their age, and the
       answer is reconciled into the nodes already on screen: one that says
       the same thing changes no node, one that differs updates in place, and
       the store is never taken back to its loading state. A press that lands
       while an intent warm is out joins it rather than asking twice.

       The Stardust pack prices are a public config that no purchase moves.
       An answer younger than `STORE_DATA_FRESH_MS` is the open's own, and it
       is not asked again (`storeOpenAdopts`).

       "Fresh" also means nothing has moved the answer since it was taken, for
       the warm too:
       - A change the store is told about (a voucher, a grant, a purchase
         answered in place) takes its read again at once, and nothing joins
         the read from before it.
       - A duel spends Moonlight and says nothing, so the lobby's warm after a
         match takes the Moonlight again however young it is
         (`markStoreReadStale`), and the next open's first frame shows today's
         figure. */
    /** @typedef {'subscription' | 'stardust' | 'moonlight'} StoreReadKind */
    /** @type {ReadonlyArray<StoreReadKind>} */
    const STORE_READ_KINDS = Object.freeze(['subscription', 'stardust', 'moonlight']);
    /* An answer younger than this is not taken again by the next warm, and
       the open keeps pack prices this young as its own. */
    const STORE_DATA_FRESH_MS = 30_000;
    /* How recently a warm read must have started for the open to adopt it as
       its own re-read: an intent warm from the hover or touch just before the
       press. Anything older, the open asks again. */
    const STORE_READ_JOIN_MS = 2_000;
    /* The one loading state's cap (docs/asset-loading.md law 2: every gate is
       capped). Past it the store paints what it has; a section still waiting
       shows its own status card and updates in place when its read lands. */
    const STORE_OPEN_HOLD_MS = 4_000;

    /**
     * @param {() => unknown} fetchAnswer
     * @param {(answer: any) => boolean} [accept]
     */
    function createStoreRead(fetchAnswer, accept = (_answer) => true) {
        /* `staleSeq`: the newest read started before something moved the
           answer. Its answer, and every older one, is not fresh. */
        return { fetchAnswer, accept, answer: undefined, answeredAt: 0, answeredSeq: 0, seq: 0, staleSeq: 0, inflight: null };
    }

    const storeReads = {
        subscription: createStoreRead(
            () => {
                const isPaid = MoonfallCloud?.users?.isPaid;
                if (typeof isPaid !== 'function') throw new Error('Subscription status API unavailable');
                return withSubscriptionStatusTimeout(isPaid.call(MoonfallCloud.users));
            },
            (status) => Boolean(status) && typeof status.isPaid === 'boolean',
        ),
        stardust: createStoreRead(() => {
            const getStardustConfig = MoonfallCloud?.stardustDenomination?.getConfig;
            if (typeof getStardustConfig !== 'function') {
                throw new Error('Stardust denomination API unavailable');
            }
            return getStardustConfig.call(MoonfallCloud.stardustDenomination);
        }),
        /* No API is an answer here: the meter shows its truthful unknown. */
        moonlight: createStoreRead(() => MoonfallCloud?.users?.getMoonlightStatus?.() ?? null),
    };
    /* Whose answers these are. Bumped when the signed-in account changes, so
       a warm taken for one account can never be painted for another. */
    let storeReadOwner = null;
    let storeReadIdentity = 0;

    function currentStoreReader() {
        return normalizeCheckoutUserId(Firebase?.getUser?.()?.uid);
    }

    function ensureStoreReadOwner() {
        const owner = currentStoreReader();
        if (owner === storeReadOwner) return;
        storeReadOwner = owner;
        storeReadIdentity += 1;
        for (const kind of STORE_READ_KINDS) {
            const read = storeReads[kind];
            read.answer = undefined;
            read.answeredAt = 0;
            read.answeredSeq = 0;
            read.inflight = null;
        }
        // The meter and the refill outlive an open; the last account's
        // figures leave with its answers.
        paintStoreMoonlight();
        const refill = document.getElementById('store-moonlight-refill');
        if (refill) refill.hidden = true;
    }

    function storeAnswer(kind) {
        return storeReads[kind].answer;
    }

    /**
     * Start one read, or join the one already out. `onSettled(ok, seq)` runs in
     * the same microtask the answer lands in, so a caller paints with it
     * without another hop. Only the NEWEST read's answer is kept: an older
     * read that lands late cannot overwrite a newer one.
     *
     * A warm joins anything already out. An open joins only a warm that
     * started a moment ago (the intent that preceded the press). A refresh —
     * something just changed the answer — never joins a read that may have
     * started before the change, and nothing joins a read that started before
     * the answer was marked stale.
     * @param {StoreReadKind} kind
     * @param {{ origin: 'warm' | 'open' | 'refresh', onSettled?: (ok: boolean, seq: number) => void }} options
     */
    function readStore(kind, { origin, onSettled }) {
        const read = storeReads[kind];
        const flight = read.inflight;
        const joinable = flight
            && flight.identity === storeReadIdentity
            && flight.seq > read.staleSeq
            && (origin === 'warm'
                || (origin === 'open'
                    && flight.origin === 'warm'
                    && Date.now() - flight.startedAt <= STORE_READ_JOIN_MS));
        if (joinable) {
            if (onSettled) flight.waiters.push(onSettled);
            return;
        }
        const next = {
            origin,
            startedAt: Date.now(),
            identity: storeReadIdentity,
            seq: ++read.seq,
            waiters: onSettled ? [onSettled] : [],
        };
        read.inflight = next;
        let pending;
        try {
            pending = read.fetchAnswer();
        } catch (error) {
            pending = Promise.reject(error);
        }
        Promise.resolve(pending).then(
            (answer) => (read.accept(answer)
                ? settleStoreRead(kind, next, true, answer)
                : settleStoreRead(kind, next, false, new Error(`Store ${kind} response invalid`))),
            (error) => settleStoreRead(kind, next, false, error),
        );
    }

    function settleStoreRead(kind, flight, ok, value) {
        const read = storeReads[kind];
        if (read.inflight === flight) read.inflight = null;
        const current = flight.identity === storeReadIdentity;
        const answered = ok && current;
        if (answered && flight.seq > read.answeredSeq) {
            read.answer = value;
            read.answeredAt = Date.now();
            read.answeredSeq = flight.seq;
        }
        /* A config the store cannot sell from (maintenance, a malformed
           ladder) is still the server's answer: it is kept and painted as
           "temporarily unavailable". The open says why. */
        if (answered && kind === 'stardust' && flight.origin !== 'warm') {
            try {
                normalizeStardustStoreConfig(value);
            } catch (error) {
                console.error('[Store] Stardust config unavailable', error);
            }
        }
        // A failed warm is silent; the open that needs the answer says so.
        if (!ok && flight.origin !== 'warm') {
            if (kind === 'stardust') console.error('[Store] Stardust config unavailable', value);
            else if (kind === 'subscription') console.error('[Store] Auth check failed', value);
            else console.warn('[Store] Could not resolve Moonlight status', value);
        }
        for (const waiter of flight.waiters) waiter(answered, flight.seq);
    }

    /**
     * An answer the store may paint as current: this account's, younger than
     * `STORE_DATA_FRESH_MS`, and taken by a read that started after the last
     * thing known to have moved it.
     * @param {StoreReadKind} kind
     */
    function storeReadFresh(kind) {
        const read = storeReads[kind];
        return read.answer !== undefined
            && read.answeredSeq > read.staleSeq
            && Date.now() - read.answeredAt < STORE_DATA_FRESH_MS;
    }

    /**
     * Something moved this answer without saying what to. It stays on screen
     * (never a blank), but the next warm or open asks again, and a read
     * already out, which started before the move, is not joined.
     * @param {StoreReadKind} kind
     */
    function markStoreReadStale(kind) {
        const read = storeReads[kind];
        read.staleSeq = read.seq;
    }

    /** The warm: every read that has no fresh answer and none on its way. */
    function warmStoreData() {
        if (Firebase?.isSignedIn?.() !== true) return;
        ensureStoreReadOwner();
        for (const kind of STORE_READ_KINDS) {
            const read = storeReads[kind];
            if (read.inflight && read.inflight.seq > read.staleSeq) continue;
            if (storeReadFresh(kind)) continue;
            readStore(kind, { origin: 'warm' });
        }
    }

    /** The lobby's data warm. It waits for the boot veil to lift — while
     *  the veil is up the link belongs to the lobby's own reads, and the veil
     *  waits for those — and then runs at once: a fetch never delays the
     *  page's `load`, so there is no reason to wait for that too.
     *
     *  The lobby asks for it on arrival and after every match. A duel spends
     *  Moonlight at its start and as it runs, and nothing announces that, so
     *  the Moonlight answer is stale here whatever its age. The pass and the
     *  packs are not moved by a duel: they are taken again only once they are
     *  older than `STORE_DATA_FRESH_MS`. */
    function warmData() {
        markStoreReadStale('moonlight');
        const veil = /** @type {{ whenReleased?: () => Promise<void> }} */ (BootVeil);
        const released = typeof veil?.whenReleased === 'function' ? veil.whenReleased() : null;
        Promise.resolve(released).then(() => { setTimeout(warmStoreData, 0); });
    }

    /** Something changed this answer: take it again, whatever is out. */
    function refreshStoreRead(kind, onSettled) {
        if (Firebase?.isSignedIn?.() !== true && !isOpen) return;
        ensureStoreReadOwner();
        readStore(kind, { origin: 'refresh', onSettled });
    }

    function claimLobbySurface(returnFocus) {
        const owner = document.body?.dataset?.lobbySurfaceOwner;
        if (owner && owner !== SURFACE_OWNER) {
            const selector = LAYERABLE_SURFACE_SELECTORS[owner];
            if (!selector || !returnFocus?.closest?.(selector)) return false;
            parentSurfaceOwner = owner;
        } else if (owner !== SURFACE_OWNER) {
            parentSurfaceOwner = null;
        }
        if (document.body) document.body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
        return true;
    }

    function releaseLobbySurface() {
        if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
            if (parentSurfaceOwner) {
                document.body.dataset.lobbySurfaceOwner = parentSurfaceOwner;
            } else {
                delete document.body.dataset.lobbySurfaceOwner;
            }
        }
        parentSurfaceOwner = null;
    }

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        /* The FALLBACK is interpolated too. It is returned verbatim whenever
           i18n has not resolved — the normal case on a cold load — and the
           ui.json wording these fallbacks mirror carries `{{name}}`
           placeholders, so a user could be shown a literal `{{count}}`. Both
           brace styles are handled because the copy uses both. */
        const source = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements || {}).reduce(
            (text, [name, value]) => String(text)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(source == null ? key : source),
        );
    }

    function escapeHtml(value) {
        return String(value ?? '').replace(/[&<>"']/g, (char) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        }[char]));
    }

    function htmlText(key, fallback, replacements = {}) {
        return escapeHtml(uiText(key, fallback, replacements));
    }

    function replaceEventHandler(target, property, type, handler, options) {
        const previous = target?.[property];
        if (typeof previous === 'function') {
            target.removeEventListener(type, previous, options);
        }
        if (!target) return;
        target[property] = handler;
        target.addEventListener(type, handler, options);
    }

    /* Every price the Store can print, so the inclusive presentation is
       all-or-nothing: a card showing a net figure under "includes 19% VAT"
       would be a worse statement than the one this replaces. The packs are
       included once their config lands, which is why this is recomputed
       rather than captured. */
    function storeDisplayAmountsMinor() {
        const amounts = passCardAmountsMinor();
        for (const subscriptionType of ['moonfall_storyteller', 'moonfall_manga']) {
            for (const interval of ['month', 'year']) {
                for (const weeklyReset of [false, true]) {
                    const minor = refillPriceMinor({ subscriptionType, interval, weeklyReset });
                    if (Number.isSafeInteger(minor) && minor > 0) amounts.push(minor);
                }
            }
        }
        for (const pack of stardustPackState.packs) {
            if (Number.isSafeInteger(pack?.amountMinor)) amounts.push(pack.amountMinor);
        }
        return amounts;
    }

    function applyDisplayTax() {
        const before = storeDisplayTax;
        storeDisplayTax = usableDisplayTax(displayTaxAnswer, storeDisplayAmountsMinor());
        /* The inclusive labels are longer than the net ones, and the Stardust
           column is sized by them (store.css). The flag carries that one fact
           to the stylesheet so the relaxation it unlocks applies in exactly the
           countries whose prices grew, and nowhere else. */
        document.getElementById('store-content')?.classList
            .toggle('is-tax-inclusive', showsTaxInclusive(storeDisplayTax));
        return before !== storeDisplayTax;
    }

    /* ASKED ONCE PER PAGE, AS EARLY AS THE STORE IS WIRED UP.
       `RegionAvailability` caches one request for the whole page and never
       rejects, so by the time anyone opens the Store the answer is already
       here and the prices paint at their final value. If it is somehow not —
       a store opened in the first moments of a cold load — the figures are
       the list prices with the copy that has always accompanied them, and
       the sections repaint once when it lands. */
    function primeDisplayTax() {
        if (displayTaxRequested) return;
        displayTaxRequested = true;
        Promise.resolve(RegionAvailability.getRegionAvailability())
            .then((region) => {
                displayTaxAnswer = region?.tax || null;
                /* REPAINT ONLY IF A FIGURE ACTUALLY MOVED. In a country that
                   collects nothing — which is the answer for most of the
                   world — this changes not one character. Where it does, the
                   figures are reconciled into the cards already on screen; a
                   store still behind its loading state simply presents with
                   them. Nothing is re-read: a tax answer is not new data
                   about the account. */
                if (!applyDisplayTax() || !isOpen) return;
                repaintStore();
            })
            .catch(() => { /* never rejects; today's presentation stands */ });
    }

    function init() {
        if (initialized) return;
        initialized = true;
        primeDisplayTax();

        replaceEventHandler(
            document,
            '__moonfallStoreTriggerHandler',
            'click',
            handleStoreTrigger
        );
        replaceEventHandler(document, '__moonfallStoreIntentPointer', 'pointerover', handleStoreIntent, { passive: true });
        replaceEventHandler(document, '__moonfallStoreIntentFocus', 'focusin', handleStoreIntent);
        replaceEventHandler(document, '__moonfallStoreIntentTouch', 'touchstart', handleStoreIntent, { passive: true });

        const closeBtn = document.getElementById('store-close-btn');
        replaceEventHandler(closeBtn, '__moonfallStoreCloseHandler', 'click', close);

        const modal = document.getElementById('store-modal');
        replaceEventHandler(modal, '__moonfallStoreBackdropHandler', 'click', (event) => {
            if (event.target === modal) close();
        });

        /* A language change is new copy, not new data: the words are
           reconciled into the nodes on screen and nothing is re-read. */
        const languageHandler = () => {
            if (!isOpen) return;
            renderPlans({ reread: false });
            const balance = document.getElementById('store-stardust-balance-value');
            renderStoreBalance(balance?.dataset?.countValue);
        };
        replaceEventHandler(
            window,
            '__moonfallStoreLanguageHandler',
            'moonfall:languagechange',
            languageHandler
        );

        /* A voucher redeemed while the store is open can change the pass
           state beneath it; the plans re-read rather than showing a stale
           Subscribe button. Closed, the warmed answer is re-taken, so the
           next open does not paint the pass the account had before. */
        replaceEventHandler(
            window,
            '__moonfallStoreSubscriptionHandler',
            'moonfall:subscription-changed',
            () => {
                billingStatusOverride = null;
                if (isOpen) renderPlans();
                else refreshStoreRead('subscription');
            }
        );
        /* The same for the Moonlight a voucher or a settled referral grants. */
        replaceEventHandler(
            window,
            '__moonfallStoreMoonlightHandler',
            'moonfall:moonlight-changed',
            () => { renderStoreMoonlight(); }
        );
        /* A new identity is a new account to read for (the warmed answers
           belong to the last one); a refreshed token for the same account
           keeps them. */
        replaceEventHandler(
            window,
            '__moonfallStoreAuthHandler',
            'moonfall:convex-auth-changed',
            () => {
                const before = storeReadIdentity;
                ensureStoreReadOwner();
                if (before !== storeReadIdentity && isOpen) renderPlans();
            }
        );
    }

    function normalizedBalance(value) {
        if (value === null || value === undefined || value === '') return null;
        const numeric = Number(value);
        return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : null;
    }

    function readTriggerBalance(trigger) {
        if (!trigger) return null;
        const countElement = trigger.matches?.('[data-count-value]')
            ? trigger
            : trigger.querySelector?.('[data-count-value]');
        const datasetBalance = normalizedBalance(countElement?.dataset?.countValue);
        if (datasetBalance !== null) return datasetBalance;

        /* Only a Stardust door's text is a Stardust figure. The lobby's
           Moonlight chip opens the store too, and its "70%" was painted into
           the Stardust pill as a balance of 70 until the profile read
           replaced it. */
        if (!trigger.matches?.('[data-open-store]')) return null;
        const matched = String(trigger.textContent || '').match(/-?\d[\d\s,.]*/)?.[0];
        if (!matched) return null;
        const digits = matched.replace(/\D/g, '');
        return digits
            ? normalizedBalance(`${matched.startsWith('-') ? '-' : ''}${digits}`)
            : null;
    }

    /* THE NUMBER ALONE. The pill used to paint "2.57K ☆" and the word
       "Stardust" beside it; the word ellipsised in French and the ☆ was
       whatever glyph the platform font had. The Stardust mark is now drawn by
       CSS (`#store-stardust-balance::before`), so the text is just the figure —
       and the accessible name carries the word and the exact balance. */
    /* Writes only what differs: the profile read on every open usually
       confirms the figure the opener already painted, and confirming it must
       not touch the pill. */
    function renderStoreBalance(value) {
        const balance = document.getElementById('store-stardust-balance-value');
        if (!balance) return;
        const container = document.getElementById('store-stardust-balance');
        const label = uiText('common.stardust', 'Stardust');
        const numeric = normalizedBalance(value);
        const compact = numeric === null ? '\u2014' : formatStardust(numeric);
        if (numeric === null) {
            if ('countValue' in balance.dataset) delete balance.dataset.countValue;
        } else if (balance.dataset.countValue !== String(numeric)) {
            balance.dataset.countValue = String(numeric);
        }
        if (balance.dataset.compactValue !== compact) balance.dataset.compactValue = compact;
        if (balance.textContent !== compact) balance.textContent = compact;
        setAttributeIfChanged(container, 'aria-label', `${label}: ${numeric === null ? '\u2014' : numeric}`);
    }

    function setAttributeIfChanged(element, name, value) {
        if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
    }

    async function refreshStoreBalance(request) {
        const getProfile = MoonfallCloud?.users?.getProfile;
        if (typeof getProfile !== 'function') return;
        try {
            const profile = await getProfile();
            if (!isOpen || request !== balanceRequest) return;
            const balance = normalizedBalance(profile?.stardust);
            if (balance !== null) renderStoreBalance(balance);
        } catch (error) {
            if (isOpen && request === balanceRequest) {
                console.warn('[Store] Unable to refresh Stardust balance:', error);
            }
        }
    }

    function handleStoreTrigger(event) {
        const source = event.target;
        const trigger = source instanceof Element ? source.closest('[data-open-store]') : null;
        if (!trigger || trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        /* A Stardust chip's "+" (the lobby chip, the Foundry's and the
           Collection's pills all open the store from a Stardust figure) lands
           on the Stardust packs: that is what the "+" beside a balance says. */
        const spotlight = trigger.matches('.user-stardust, .stardust-display, .moon-dream-stardust, .collection-stardust')
            ? 'stardust'
            : null;
        // A press on the chip's "+" disc gets the disc's own beat; the chip
        // opens the store either way.
        if (spotlight && trigger instanceof HTMLElement && isPlusPress(trigger, event)) pulsePlus(trigger);
        open({
            returnFocus: trigger,
            initialBalance: readTriggerBalance(trigger),
            spotlight,
        });
    }

    /* THE LOBBY'S TOP BAR IS COVERED, NEVER HIDDEN. #user-controls (the
       avatar, the name, the Stardust and Moonlight chips, Quests, Community,
       the menu) and the language selector sit at z-index 9999; the Store is a
       full-screen layer at 30000 and paints over them the way every modal
       paints over what is under it. The Store used to set them `display: none`
       in the press's frame and back at the Close press, while it was still
       fading out over them: every quick open and close blinked the whole bar
       off and on (snappy round 15, `store-fast-open-close`: 26-119 flickers in
       every Chrome, Firefox and WebKit cell). That hide dates from the first
       full-screen Store (June 2026), which sat BELOW the bar. The bar is inert
       and hidden from assistive tech under the open Store like everything
       else behind it (isolateStoreBackground). */

    /* The Store's entrance starts at this opacity (store.css
       `store-modal-enter`), over this long, so the press's first frame already
       shows it arriving. */
    const STORE_ENTER_FROM_OPACITY = 0.35;
    const STORE_ENTER_MS = 220;
    /* The Store's exit starts at this opacity (store.css `store-modal-exit`),
       over this long. */
    const STORE_EXIT_FROM_OPACITY = 0.65;
    const STORE_EXIT_MS = 180;

    function prefersReducedMotion() {
        try {
            return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
        } catch (_) {
            return false;
        }
    }

    function stopStoreResumeFade() {
        const fade = storeResumeFade;
        storeResumeFade = null;
        try {
            fade?.cancel?.();
        } catch (_) {
            // Already over.
        }
    }

    /**
     * A STORE REOPENED ON ITS WAY OUT TURNS AROUND WHERE ITS FADE STANDS.
     * Pressed again before its exit ended, the Store never left the screen:
     * restarting its CSS entrance replayed an arrival over it, and from the
     * exit's first 65% dropped it back to 35% first (an opacity blink). The
     * entrance is switched off (`store-modal-resumed`) and the fade runs from
     * the opacity on screen now — or from the entrance's own first step, when
     * the exit has all but finished — to fully shown, over the rest of the
     * entrance's time. Reduced motion: simply shown.
     */
    function reopenFade(modal, fromOpacity) {
        stopStoreResumeFade();
        if (prefersReducedMotion() || typeof modal.animate !== 'function') return;
        const from = Math.min(1, Math.max(STORE_ENTER_FROM_OPACITY, Number(fromOpacity) || 0));
        if (from >= 1) return;
        const duration = Math.round(STORE_ENTER_MS * ((1 - from) / (1 - STORE_ENTER_FROM_OPACITY)));
        try {
            storeResumeFade = modal.animate(
                [{ opacity: from }, { opacity: 1 }],
                { duration, easing: 'ease-out' },
            );
        } catch (_) {
            storeResumeFade = null;
        }
    }

    /** The Store's opacity on screen now (its entrance, exit or a fade of
        store.js's own included), or null when it cannot be read. */
    function shownOpacity(modal) {
        try {
            const value = Number.parseFloat(window.getComputedStyle(modal).opacity);
            return Number.isFinite(value) ? value : null;
        } catch (_) {
            return null;
        }
    }

    /** The opacity the Store's exit has reached, read before the exit is taken off. */
    function closingOpacity(modal) {
        return shownOpacity(modal) ?? 0;
    }

    /**
     * A STORE CLOSED BEFORE IT REACHED THE EXIT'S FIRST STEP FADES OUT FROM
     * WHERE IT STANDS (review of wave D, 28 September 2026 — the mirror of
     * `reopenFade`). The exit starts at 65%, so a Store closed while its
     * entrance or a turn-around still stood below that (a quick Close on a
     * press, a Close during a reopened Store's fade) jumped UP to 65% before
     * it went: an opacity blink on the way out. The CSS exit is switched off
     * (`store-modal-early-exit`) and the fade runs from the opacity on screen
     * to nothing, over the part of the exit's time that distance takes. A
     * Store that arrived, an opacity that cannot be read, reduced motion (no
     * exit to jump into) or an engine without Web Animations: the designed
     * exit, as before.
     */
    function earlyExitFade(modal, shownAt) {
        if (shownAt === null || shownAt >= STORE_EXIT_FROM_OPACITY) return;
        if (prefersReducedMotion() || typeof modal.animate !== 'function') return;
        const from = Math.max(0, shownAt);
        try {
            if (from > 0) {
                storeResumeFade = modal.animate(
                    [{ opacity: from }, { opacity: 0 }],
                    {
                        duration: Math.max(1, Math.round(STORE_EXIT_MS * (from / STORE_EXIT_FROM_OPACITY))),
                        easing: 'ease-in',
                    },
                );
            }
            modal.classList.add('store-modal-early-exit');
        } catch (_) {
            storeResumeFade = null;
        }
    }

    function isVisibleOverlay(element) {
        if (element.inert || element.getAttribute('aria-hidden') === 'true' || element.hidden) return false;
        const style = window.getComputedStyle?.(element);
        return style?.display !== 'none' && style?.visibility !== 'hidden';
    }

    /* ANOTHER MODAL OWNS THE KEYBOARD. The store's capture-phase keydown used
       to yield only to the rotate and consent overlays, so any dialog opened
       over the store — the voucher dialog, its reward screen, the upgrade
       Confirm — lost Escape (the store closed underneath) and Tab (focus jumped
       into the store). Any visible modal outside the store now takes priority. */
    function foreignModalOwnsInput({ except = null } = {}) {
        const modal = document.getElementById('store-modal');
        return Array.from(document.querySelectorAll(
            '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'
        )).some((element) => (
            element !== modal
            && element !== except
            && !(modal && modal.contains(element))
            && isVisibleOverlay(element)
        ));
    }

    /* The surface the store is about to layer over, resolved from the control
       that opened it (the Collection header's Stardust chip, the Foundry's
       balance pill). It is a modal dialog in its own right, and it is NOT a
       foreign one: `LAYERABLE_SURFACE_SELECTORS` says the store opens on top
       of it. The gate below had counted it as foreign since the voucher work
       widened `foreignModalOwnsInput` for the keyboard handler, so `open()`
       answered `false` to the Collection chip and the click did nothing. */
    function layeredParentSurface(returnFocus) {
        const owner = document.body?.dataset?.lobbySurfaceOwner;
        const selector = owner && owner !== SURFACE_OWNER ? LAYERABLE_SURFACE_SELECTORS[owner] : null;
        if (!selector) return null;
        return returnFocus?.closest?.(selector) || null;
    }

    function hasActiveStorePrioritySurface({ layeredParent = null } = {}) {
        if (OrientationGuard?.isActive?.()) return true;
        if (foreignModalOwnsInput({ except: layeredParent })) return true;
        return Array.from(document.querySelectorAll('#rotate-overlay, .consent-gate-overlay')).some(isVisibleOverlay);
    }

    function getStoreBackgroundElements(modal) {
        if (!modal || !document.body) return [];
        return Array.from(document.body.children).filter((element) => (
            element !== modal
            && !element.contains(modal)
            && element.id !== 'rotate-overlay'
            && !element.classList?.contains('consent-gate-overlay')
            && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName)
        ));
    }

    function isolateStoreBackground(modal) {
        if (storeBackgroundState !== null) return;
        storeBackgroundState = getStoreBackgroundElements(modal).map((element) => ({
            element,
            hadInertAttribute: element.hasAttribute('inert'),
            inertValue: 'inert' in element ? element.inert : null,
            ariaHidden: element.getAttribute('aria-hidden'),
        }));

        storeBackgroundState.forEach(({ element }) => {
            if ('inert' in element) element.inert = true;
            element.setAttribute('inert', '');
            element.setAttribute('aria-hidden', 'true');
        });
    }

    function restoreStoreBackground() {
        if (storeBackgroundState === null) return;
        const states = storeBackgroundState;
        storeBackgroundState = null;

        states.forEach(({ element, hadInertAttribute, inertValue, ariaHidden }) => {
            if ('inert' in element && inertValue !== null) element.inert = inertValue;
            if (hadInertAttribute) element.setAttribute('inert', '');
            else element.removeAttribute('inert');

            if (ariaHidden === null) element.removeAttribute('aria-hidden');
            else element.setAttribute('aria-hidden', ariaHidden);
        });
    }

    function getStoreFocusableElements(modal) {
        if (!modal) return [];
        return Array.from(modal.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => element.getAttribute('aria-hidden') !== 'true');
    }

    function removeStoreKeyHandler() {
        if (!storeKeyHandler) return;
        document.removeEventListener('keydown', storeKeyHandler, true);
        if (document.__moonfallStoreKeyHandler === storeKeyHandler) {
            delete document.__moonfallStoreKeyHandler;
        }
        storeKeyHandler = null;
    }

    function installStoreKeyHandler(modal) {
        removeStoreKeyHandler();
        storeKeyHandler = (event) => {
            if (!isOpen || hasActiveStorePrioritySurface()) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                close();
                return;
            }
            if (event.key !== 'Tab') return;

            const focusable = getStoreFocusableElements(modal);
            if (focusable.length === 0) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }
            const currentIndex = focusable.indexOf(document.activeElement);
            const nextIndex = event.shiftKey
                ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
                : (currentIndex < 0 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
            event.preventDefault();
            event.stopImmediatePropagation();
            focusable[nextIndex].focus();
        };
        replaceEventHandler(
            document,
            '__moonfallStoreKeyHandler',
            'keydown',
            storeKeyHandler,
            true
        );
    }

    function resolveStoreReturnFocus(preferred) {
        if (preferred?.isConnected) return preferred;
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) {
            return active;
        }
        return document.getElementById('user-stardust');
    }

    /* NO EMAIL-NEWS BOX HERE. The consent merge (4828e775) offered the
       email-news choice beside the buy buttons as an EU/UK point-of-sale
       "soft opt-in"; the owner's ruling: such choices belong on the screen
       where a player accepts all of them — the consent gate — and in the
       Privacy & Data panel, not in a store. A checkout therefore carries no
       email choice, and the server records none for it. */

    function open({ returnFocus = null, initialBalance = null, spotlight = null } = {}) {
        // Belt and braces for a caller that opens the Store without init().
        primeDisplayTax();
        /* The lobby's hover surfaces sit under this modal. A covered element
           never fires `mouseleave`, so one left open stays open and is still
           there when the store closes. */
        closeMoonlightCard();
        if (document.body?.classList.contains('game-transition-pending')
            || document.body?.classList.contains('game-active')) {
            return false;
        }
        const modal = document.getElementById('store-modal');
        if (!modal || hasActiveStorePrioritySurface({ layeredParent: layeredParentSurface(returnFocus) })) {
            return false;
        }
        if (isOpen) {
            pendingSpotlight = spotlight === 'refill' || spotlight === 'stardust' ? spotlight : null;
            pendingSpotlightExplain = false;
            renderPlans({ reread: 'open' });
            return true;
        }
        if (!claimLobbySurface(returnFocus)) return false;
        pendingSpotlight = spotlight === 'refill' || spotlight === 'stardust' ? spotlight : null;
        pendingSpotlightExplain = false;
        // WebKit/Safari does not focus <button>s on mouse click, so the active
        // element can still be <body>. A delegated trigger supplies the exact
        // invoking control; direct callers retain the active-element fallback.
        storeReturnFocus = resolveStoreReturnFocus(returnFocus);
        isOpen = true;
        storePresented = false;
        warmStoreArt(true);
        /* The figure the player just pressed, or the one the lobby chip holds
           (a door with no Stardust figure on it, like the Moonlight chip) —
           never a blank the profile read then fills in. */
        renderStoreBalance(
            normalizedBalance(initialBalance)
            ?? readTriggerBalance(storeReturnFocus)
            ?? normalizedBalance(lastKnownStardust())
        );
        const request = ++balanceRequest;
        void refreshStoreBalance(request);
        if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
        }
        /* ON SCREEN IN THE FRAME THE PRESS PRODUCES. The entrance used to be
           an opacity transition from 0, started by a 10 ms timer after
           `display` applied — two to three frames of nothing after the press
           (130 ms measured on a warm cache). `store.css` now runs the
           entrance as an animation that starts visible, so `visible` goes on
           with `display` and the first frame already shows the store. A Store
           still fading out turns around instead (reopenFade). */
        const turningAround = modal.classList.contains('is-closing') && modal.style.display === 'flex';
        const fadeAt = turningAround ? closingOpacity(modal) : 1;
        modal.style.display = 'flex';
        modal.setAttribute('aria-hidden', 'false');
        modal.classList.remove('is-closing', 'store-modal-early-exit');
        modal.classList.toggle('store-modal-resumed', turningAround);
        modal.classList.add('visible');
        if (turningAround) reopenFade(modal, fadeAt);
        const container = document.getElementById('store-content');
        if (container) container.scrollTop = 0;
        installStoreKeyHandler(modal);
        /* Focus first, then the content: an opener's "+" that lands on a
           control (`settlePendingSpotlight`) keeps that focus.

           WITHOUT A SCROLL. WebKit scrolls an element it focuses into view at
           its next rendering update, and when focus moves on before that
           update it scrolls the NEW element, whatever that element's own
           focus asked. The close button is pinned in the corner and never
           needs one; focused plainly, it handed its reveal to the Stardust
           packs' Buy button the chip's "+" focuses next, and a landscape
           iPhone opened the store scrolled 17px, both section titles cut in
           half under the balance pills (a player's session, 24 October). */
        const closeButton = document.getElementById('store-close-btn');
        if (closeButton) closeButton.focus({ preventScroll: true });
        // A door's open: painted from what the store holds, asking again
        // behind it for the pass and the Moonlight.
        renderPlans({ reread: 'open' });
        isolateStoreBackground(modal);
        return true;
    }

    function close({ immediate = false, restoreFocus = true } = {}) {
        if (!isOpen) return false;
        isOpen = false;
        pendingSpotlight = null;
        pendingSpotlightExplain = false;
        // A card anchored to the store's meter or refill must leave with it.
        closeMoonlightCard();
        billingStatusOverride = null;
        balanceRequest += 1;
        storeOpenToken += 1;
        storePresented = false;
        clearStoreHold();
        removeStoreKeyHandler();
        unwatchStoreHeads();
        if (closeTimer) clearTimeout(closeTimer);
        const modal = document.getElementById('store-modal');
        if (modal) {
            // Where the fade stands, read before a turn-around is stopped.
            const shownAt = immediate ? null : shownOpacity(modal);
            // A turn-around still running would hold the exit off (a Web
            // Animation composites over the CSS one).
            stopStoreResumeFade();
            modal.classList.remove('visible', 'store-modal-resumed', 'store-modal-early-exit');
            modal.setAttribute('aria-hidden', 'true');
            if (immediate) {
                modal.classList.remove('is-closing');
                modal.style.display = 'none';
                closeTimer = null;
            } else {
                // The exit (store.css) steps down on the first frame, or
                // fades from where the Store stands (earlyExitFade).
                modal.classList.add('is-closing');
                earlyExitFade(modal, shownAt);
                closeTimer = setTimeout(() => {
                    if (!isOpen) {
                        modal.style.display = 'none';
                        modal.classList.remove('is-closing', 'store-modal-early-exit');
                    }
                    closeTimer = null;
                }, 300);
            }
        }
        restoreStoreBackground();
        /* Resolved BEFORE the release hands the surface back, while the
           parent owner is still known: the Collection the store sat on is
           visible again now and must not count as a foreign modal, or the
           chip that opened the store never gets its focus back. */
        const layeredParent = parentSurfaceOwner
            ? document.querySelector(LAYERABLE_SURFACE_SELECTORS[parentSurfaceOwner] || '')
            : null;
        releaseLobbySurface();
        const returnFocus = storeReturnFocus;
        storeReturnFocus = null;
        if (restoreFocus
            && !hasActiveStorePrioritySurface({ layeredParent })
            && returnFocus
            && returnFocus.isConnected
            && typeof returnFocus.focus === 'function') {
            returnFocus.focus();
        }
        return true;
    }

    const checkoutInflight = new Set();

    /* The server quotes the exact payable upgrade before consent. The signed
       quote binds execution to that price, period and weekly section. */
    async function beginCheckout(planType) {
        if (!Firebase.isSignedIn()) {
            alert(uiText('store.signInToPurchase', 'Please sign in to purchase.'));
            return;
        }
        if (checkoutInflight.has(planType)) {
            return;
        }
        checkoutInflight.add(planType);
        const clear = () => {
            setTimeout(() => checkoutInflight.delete(planType), 3000);
        };
        let upgradeConsent = null;
        let upgradeQuoteId = null;
        try {
            /* Every new paid plan bought during a live gift starts billing
               when the gift ends, including its final hour. */
            if (lastSubscriptionState === 'complimentary' && isSubscriptionPlan(planType)) {
                const ok = await Confirm.ask(
                    uiText('voucher.storeAfterGiftTitle', 'Subscribe for when your gift ends?'),
                    {
                        detail: uiText(
                            'voucher.storeAfterGiftDetail',
                            'Your new pass starts its normal billing cycle when your gift ends on {{date}}. Your card is charged then, not today. Unused gift Moonlight expires; it does not carry into the paid bar.',
                            { date: formatPassEndDate(complimentaryStatus?.expiresAt) },
                        ),
                        confirmLabel: uiText('voucher.storeReplaceGo', 'Continue to payment'),
                        cancelLabel: uiText('common.cancel', 'Cancel'),
                    },
                );
                if (!ok) { checkoutInflight.delete(planType); return; }
            }
            if (isImmediateUpgradeCharge(lastSubscriptionState, planType, activeBillingInterval)) {
                let quote;
                try {
                    const idToken = await Firebase.getIdToken();
                    if (!idToken) throw new Error('upgrade_quote_auth_required');
                    const response = await fetch('/api/subscription/upgrade-quote', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
                        body: JSON.stringify({ planType }),
                    });
                    const data = await response.json();
                    if (response.status === 409 && data.code === 'upgrade_quote_expired' && data.refunded === true) {
                        checkoutInflight.delete(planType);
                        alert(uiText('store.upgradeRefunded', 'A full refund has been issued because your plan changed during payment. No upgrade or extra Moonlight was granted. Review your current plan before trying again.'));
                        renderPlans();
                        return;
                    }
                    if (response.ok && data.upgraded === true) {
                        checkoutInflight.delete(planType);
                        billingStatusOverride = null;
                        alert(uiText('store.upgradeDone', 'Your pass is upgraded. The extra Moonlight is already yours.'));
                        renderPlans();
                        return;
                    }
                    const pendingUrl = response.ok ? pendingUpgradeInvoiceUrl(data, planType) : null;
                    if (pendingUrl) {
                        rememberCheckoutReturn(createCheckoutReturnHint());
                        checkoutInflight.delete(planType);
                        window.location.href = pendingUrl;
                        return;
                    }
                    quote = response.ok ? readUpgradeQuote(data) : null;
                } catch (_) { quote = null; }
                if (!quote) {
                    checkoutInflight.delete(planType);
                    alert(uiText('store.upgradeQuoteUnavailable', 'Could not confirm the upgrade price. Please try again. Nothing was charged.'));
                    renderPlans();
                    return;
                }
                const price = upgradePriceText(quote, i18n?.getLanguage?.() || 'en');
                const detail = uiText(
                    'store.upgradeConfirmDetail',
                    'Pay {{price}} now for section {{section}}/{{total}} and the sections still to come. Completed sections are excluded; the current section is charged in full. Your renewal date stays unchanged: {{date}}. The upgraded weekly quota is added to your actual unused Moonlight. Your weekly clock stays unchanged; at the next weekly reset, unused Moonlight expires and the bar returns to the upgraded allowance.',
                    { price, section: quote.currentSection, total: quote.totalSections, date: formatPassEndDate(quote.currentPeriodEndAt) },
                );
                /* THE EXPRESS REQUEST, collected here because no Stripe page
                   opens for an in-place change (launch audit round 3,
                   2026-09-26). The upgrade charge carries its own 14-day
                   withdrawal right; without this box a withdrawal would refund
                   it in full while its Moonlight had already been played. The
                   server refuses the upgrade without it and records the version
                   on the subscription, where the webhook reads it back. */
                const ok = await Confirm.ask(
                    uiText('store.upgradeConfirmTitle', 'Upgrade your pass?'),
                    {
                        detail,
                        requiredCheck: {
                            label: uiText(
                                'store.upgradeConsent',
                                'I expressly request that the upgraded pass starts now, during the 14-day withdrawal period. If I withdraw within 14 days, I pay only for the time already elapsed. Its Moonlight resets to 100% weekly. Unused Moonlight expires at the next weekly reset.',
                            ),
                        },
                        confirmLabel: uiText('store.upgradeConfirmGo', 'Pay {{price}} and upgrade', { price }),
                        cancelLabel: uiText('common.cancel', 'Cancel'),
                    },
                );
                /* Cleared immediately rather than on the 3s timer: a player who
                   says no should be able to press again at once. */
                if (!ok) { checkoutInflight.delete(planType); return; }
                upgradeConsent = LEGAL_VERSIONS.purchaseConsent;
                upgradeQuoteId = quote.quoteId;
            }
            const result = checkout(planType, { upgradeConsent, upgradeQuoteId });
            if (result && typeof result.then === 'function') {
                return result.then((value) => { clear(); return value; }, (err) => { clear(); throw err; });
            }
            clear();
            return result;
        } catch (err) {
            clear();
            throw err;
        }
    }

    function buildSubscriptionPlans(state) {
        if (state === 'pending') {
            return `
                <div class="plan-card highlight plan-card--status" role="status" aria-live="polite" aria-busy="true">
                    ${buildPlanArt('pass-manga.webp', '')}
                    <div class="store-subscription-status">
                        <span class="store-status-spinner" aria-hidden="true"></span>
                        <p class="store-status-copy">${htmlText('store.updatingStatus', 'Updating status...')}</p>
                    </div>
                </div>
            `;
        }

        if (state === 'error') {
            return `
                <div class="plan-card highlight plan-card--status plan-card--status-error" role="status" aria-live="polite">
                    ${buildPlanArt('pass-manga.webp', '')}
                    <p class="store-status-copy">${htmlText('store.statusUnavailable', 'Subscription status is unavailable.')}</p>
                    <button type="button" class="plan-btn primary store-status-retry" data-store-action="retry-status">${htmlText('store.retryStatus', 'Try again')}</button>
                </div>
            `;
        }

        if (state === 'payment_disputed') {
            const copy = accountOnHoldCopy({ passOnHold: Boolean(activePassTier) }, uiText);
            return `
                <div class="plan-card highlight plan-card--status plan-card--status-error" role="status" aria-live="polite">
                    ${buildPlanArt(activePassTier === 'moonfall_storyteller' ? 'pass-storyteller.webp' : 'pass-manga.webp', '')}
                    <p class="store-status-copy">${escapeHtml(copy.line)}</p>
                    ${activePassTier && !complimentaryStatus ? `<button class="plan-btn primary" data-store-action="manage">${htmlText('store.manageSubscription', 'Manage Subscription')}</button>` : ''}
                </div>
            `;
        }

        if (state === 'needs_management') {
            return `
                <div class="plan-card highlight plan-card--status plan-card--status-error" role="status" aria-live="polite">
                    ${buildPlanArt('pass-manga.webp', htmlText('store.mangaPass', 'Manga Pass'))}
                    <p class="store-status-copy">${htmlText('store.billingNeedsAttention', 'Your Manga Pass billing needs attention before you can subscribe again.')}</p>
                    <button class="plan-btn primary" data-store-action="manage">${htmlText('store.manageSubscription', 'Manage Subscription')}</button>
                    <p class="plan-note">${htmlText('store.subscriptionManageNote', 'Cancel, update payment method, or view billing history via Stripe.')}</p>
                </div>
            `;
        }

        /* A MANGA PASS HOLDER STILL SEES THE CARDS. This used to return a
           single full-width "your pass is active" panel, so a subscriber had
           no way to reach the annual price of their own pass — they had to
           cancel and re-buy. They now get the same two cards as everyone
           else, with their own marked ACTIVE and, on the Yearly tab, a real
           "Switch to yearly" button. */
        /* TWO TIERS, one period control, one button each. The markup lives in
           `store-pass-cards.js` so it can be asserted as rendered output —
           a source grep cannot see whether the selected period reaches the
           plan the button will actually charge. */
        /* A COMPLIMENTARY pass — a redeemed voucher — marks its card too, but
           there is no Stripe customer behind it: Manage would open a portal
           that answers 409. Both cards offer Subscribe for after the gift,
           and the held card says when it ends. */
        if (state === 'complimentary') {
            return buildPassCards({
                htmlText,
                tax: storeDisplayTax,
                period: currentPassPeriod(),
                activeTier: complimentaryStatus?.tier || null,
                activeInterval: null,
                weeklyReset: weeklyStorePolicy(),
                giftFourWeeks: String(complimentaryStatus?.giftPolicyVersion || '') >= '2026-10-02-v1',
                complimentary: true,
                complimentaryEndsAt: formatPassEndDate(complimentaryStatus?.expiresAt),
                giftPlan: describeGiftPlan(complimentaryStatus),
            });
        }
        return buildPassCards({
            htmlText,
            tax: storeDisplayTax,
            period: currentPassPeriod(),
            activeTier: state === 'storyteller' ? 'moonfall_storyteller'
                : (state === 'subscribed' ? 'moonfall_manga' : null),
            activeInterval: activeBillingInterval,
            weeklyReset: weeklyStorePolicy(),
        });
    }

    /* The plan behind a gift, dated for the card (store-pass-cards.js). */
    function describeGiftPlan(status) {
        const deferred = status?.deferred;
        if (deferred?.tier) {
            return { kind: 'deferred', tier: deferred.tier, startsAt: formatPassEndDate(deferred.startsAt), renewsAt: formatPassEndDate(deferred.renewsAt), status: deferred.status };
        }
        const scheduled = status?.scheduled;
        if (scheduled?.tier) return { kind: 'scheduled', tier: scheduled.tier, startsAt: formatPassEndDate(scheduled.startsAt) };
        return null;
    }

    function formatPassEndDate(ms) {
        const value = Number(ms);
        if (!Number.isFinite(value) || value <= 0) return '';
        try {
            return new Intl.DateTimeFormat(i18n?.getLanguage?.() || 'en', { dateStyle: 'medium' }).format(new Date(value));
        } catch (_) {
            return new Date(value).toLocaleDateString();
        }
    }

    function isSubscriptionPlan(planType) {
        return /^(storyteller|manga)_(monthly|yearly)$/.test(String(planType || ''));
    }

    function normalizeStardustStoreConfig(config) {
        if (config?.status !== 'ready' || !Array.isArray(config.packs)) {
            throw new Error('Stardust purchases are unavailable');
        }
        const denominationVersion = Number(config.denominationVersion);
        if (!Number.isSafeInteger(denominationVersion) || denominationVersion <= 0) {
            throw new Error('Invalid Stardust denomination');
        }
        const bySku = new Map();
        config.packs.forEach((pack) => {
            if (!pack || typeof pack.sku !== 'string' || bySku.has(pack.sku)) {
                throw new Error('Invalid Stardust pack configuration');
            }
            bySku.set(pack.sku, pack);
        });
        if (bySku.size !== STARDUST_PACK_PRESENTATION.length) {
            throw new Error('Invalid Stardust pack configuration');
        }
        return STARDUST_PACK_PRESENTATION.map((presentation) => {
            const pack = bySku.get(presentation.sku);
            if (
                !pack
                || !Number.isSafeInteger(pack.amount)
                || pack.amount <= 0
                || !Number.isSafeInteger(pack.amountMinor)
                || pack.amountMinor <= 0
            ) {
                throw new Error('Invalid Stardust pack configuration');
            }
            /* THE PRICE STRINGS ARE NOT BUILT HERE. What a pack costs depends
               on the buyer's country once tax is displayed inclusively, and
               this config is normalised once while the region answer may
               arrive after it — so the amount is kept and the figures are
               formatted at paint (`stardustPackPrices`). */
            return {
                ...presentation,
                amount: pack.amount,
                amountMinor: pack.amountMinor,
            };
        });
    }

    /* What this buyer pays for a pack, as the two figures the card shows: the
       button's compact price and the subtitle's exact one. Identical to the
       list price wherever no tax is collected. */
    function stardustPackPrices(pack) {
        const totalMinor = displayTotalMinor(pack.amountMinor, storeDisplayTax);
        const wholeDollars = totalMinor / 100;
        return {
            price: `$${Number.isInteger(wholeDollars) ? wholeDollars : wholeDollars.toFixed(2)}`,
            usd: `$${wholeDollars.toFixed(2)} USD`,
        };
    }

    function buildStardustPlans(state) {
        if (state.status === 'pending') {
            return `
                <div class="plan-card plan-card--status" role="status" aria-live="polite" aria-busy="true">
                    <span class="store-status-spinner" aria-hidden="true"></span>
                    <p class="store-status-copy">${htmlText('store.updatingStatus', 'Updating status...')}</p>
                </div>
            `;
        }
        if (state.status !== 'ready') {
            return `
                <div class="plan-card plan-card--status plan-card--status-error" role="status" aria-live="polite">
                    <p class="store-status-copy">${htmlText('store.stardustUnavailable', 'Stardust purchases are temporarily unavailable.')}</p>
                </div>
            `;
        }

        return state.packs.map((pack) => {
        const { price, usd: usdPrice } = stardustPackPrices(pack);
        return `
            <div class="plan-card">
                <!-- FIRST, so the badge owns the strip above the artwork,
                     exactly as it does on the Manga Pass cards. -->
                ${pack.popular ? `<div class="plan-badge" style="background: #9b59b6; color: #fff;">${htmlText('store.popular', 'POPULAR')}</div>` : ''}
                ${buildPlanArt(pack.icon, htmlText(pack.titleKey, pack.titleFallback))}
                <div class="plan-title">${htmlText(pack.titleKey, pack.titleFallback)}</div>
                <!-- "1K" and the Stardust glyph, not "1K Stardust": the unit word
                     repeated the title and wrapped the amount in fr/de/pt. The
                     glyph is the balance pill's, so the amount reads as the
                     currency the player holds. What is SPOKEN is the exact
                     amount and the word, from a clipped span — an aria-label on
                     a <div> is name-prohibited and the tree dropped it. -->
                <div class="plan-price" data-count-value="${pack.amount}"><span class="plan-price-amount" aria-hidden="true">${formatStardust(pack.amount)}</span><span class="stardust-glyph" aria-hidden="true"></span><span class="mf-sr-only">${pack.amount} ${htmlText('store.stardustUnit', 'Stardust')}</span></div>
                <div class="plan-price-subtitle">${usdPrice}</div>
                <!-- Prices are CHARGED tax-exclusive (stripeCheckoutTax:
                     tax_behavior 'exclusive' + automatic_tax). Where the
                     buyer's country is taxed, the figures above are the
                     totals Stripe will charge and this line names the rate;
                     where it is not, they are the list prices and this line
                     is the "added at checkout" sentence it always was. -->
                <div class="plan-tax-note">${taxNoteText(storeDisplayTax, htmlText)}</div>
                <button class="plan-btn" data-store-action="checkout" data-plan="${pack.sku}">${htmlText('store.buyFor', 'Buy for ' + price, { price })}</button>
            </div>
        `;
        }).join('');
    }

    /* Only the unsubscribed state has a price to choose. A pass holder
       cannot buy a second subscription (`server.js` answers 409
       `already_subscribed`), so a period control on their screen would move
       nothing — and a control that changes nothing is a lie about what is
       available. */
    /* A holder still has choices — the tier above, and the annual price of
       their own pass — so the period control belongs on their screen too. */
    const PERIOD_CHOOSABLE_STATES = new Set(['unsubscribed', 'storyteller', 'subscribed', 'complimentary']);

    /**
     * The whole pass section, head included.
     *
     * The period control lives in the HEAD rather than in a wrapper around the
     * cards, because `.store-plans` is placed by `grid-row` inside a SUBGRID
     * shared with the Stardust column — wrapping it makes it stop being a
     * direct child, the row placement silently stops applying, and the cards
     * land wherever auto-placement puts them. That is the exact shape of the
     * break the owner reported on mobile.
     *
     * The control appears only when something is actually buyable. Offering
     * Monthly/Yearly to a pass holder would be a control that changes nothing.
     */
    function buildPassSection(subscriptionState) {
        const selector = PERIOD_CHOOSABLE_STATES.has(subscriptionState)
            ? buildPassPeriodSelector({
                htmlText,
                period: currentPassPeriod(),
                /* Server-declared, never a client constant: the deployment
                   decides whether it is willing to sell a twelve-month
                   commitment, and `/env.js` is how it says so. Absent reads as
                   OFF, matching the server default. */
                yearlyEnabled: window.MoonfallEnv?.yearlyBillingEnabled === true,
              })
            : '';
        return `
            <div class="store-section__head">
                <h3 class="store-section__title">${htmlText('store.passes', 'Passes')}</h3>
                <p class="store-section__sub">${htmlText('store.passesTagline', 'Moonlight is your play. Both passes give more of it.')}</p>
                ${selector}
            </div>
            <div class="store-plans">${buildSubscriptionPlans(subscriptionState)}</div>
        `;
    }

    function buildStoreContent(subscriptionState) {
        /* The section sells BOTH passes now, so its heading cannot be one of
           their names. It also carries the only definition of Moonlight a
           player meets before they are asked to pay for more of it — without
           that line, a meter with a number beside it reads as a currency. */
        const passSection = `
            <section class="store-section store-section--pass">${buildPassSection(subscriptionState)}</section>
        `;

        const stardustSection = `
            <section class="store-section store-section--stardust">
                <div class="store-section__head">
                    <h3 class="store-section__title">${htmlText('store.stardustCredits', 'Stardust Credits')}</h3>
                    <p class="store-section__sub">${htmlText('store.stardustDesc', 'Credits for crafting cards and generating art.')}</p>
                </div>
                <div class="store-stardust">
                    ${buildStardustPlans(stardustPackState)}
                </div>
            </section>
        `;

        /* Standard pre-purchase legal links. Statutory rights remain available;
           checkout does not claim a withdrawal waiver without a separate valid
           flow.

           THE RENEWAL SENTENCE IS NOT OPTIONAL. Auto-renewal has to be
           disclosed before purchase, and naming "billing, cancellation" as
           topics described in the linked Terms is a pointer rather than a
           disclosure — the store previously contained the word "renew" zero
           times. It also sits opposite a Refill path that is required to say
           "One time. This does not renew." on three surfaces; the two claims
           only stay legible if both are actually made. */
        const disclosure = `
            <div class="store-disclosure-footer">
                <p class="store-disclosure">
                    <span class="store-tax-note">${taxNoteText(storeDisplayTax, htmlText)}</span>
                    <span class="store-renew-note">${htmlText('store.autoRenewNotice', 'Passes renew automatically until you cancel.')}</span>
                    <span class="store-disclosure-copy">${htmlText('store.disclosurePrefix', 'Purchases are delivered immediately. Billing, cancellation, refunds, and applicable consumer rights are described in the')}</span>
                    <span class="store-disclosure-links">
                        <a href="/terms" target="_blank" rel="noopener">${htmlText('store.terms', 'Terms of Service')}</a>
                        <span>${htmlText('store.disclosureMiddle', 'and')}</span>
                        <a href="/privacy" target="_blank" rel="noopener">${htmlText('store.privacy', 'Privacy Policy')}</a>${htmlText('store.disclosureSuffix', '.')}
                    </span>
                    <button type="button" class="store-withdraw-link" data-store-withdraw>${htmlText('withdrawal.withdrawButton', 'Withdraw from contract here')}</button>
                </p>
            </div>
        `;

        return passSection + stardustSection + disclosure;
    }

    /* The refill button lives in the store CHROME rather than in the plan
       grid, so it is wired once at open instead of on every re-render. */
    function wireStoreRefill() {
        const refill = document.getElementById('store-moonlight-refill');
        if (!refill || refill.dataset.wired === '1') return;
        refill.dataset.wired = '1';
        /* Say what is bought before paying, including the active cadence.
           Weekly refills arrive now and expire with this week; their purchase
           count lasts for the month. Legacy monthly cycles keep their terms. */
        refill.addEventListener('click', async () => {
            /* The Moonlight card anchored to the refill is left to this click
               (a press on its own anchor does not dismiss it): the confirm
               covers the store, and a covered card never sees mouseleave. */
            closeMoonlightCard();
            const ok = await Confirm.ask(
                uiText('moonlight.refillConfirmTitle', 'Refill Moonlight?'),
                {
                    /* THE PRICE SENTENCE BELONGS HERE TOO. The pill states a
                       figure and this dialog is the last screen before a
                       payment page, so it repeats what that figure includes —
                       the rate where tax is shown inclusively, the "added at
                       checkout" sentence where it is not. */
                    detail: `${storeMoonlightStatus?.weeklyReset === true ? uiText(
                        'moonlight.refillConfirmDetailWeekly',
                        "Adds one week's Moonlight immediately. One-time purchase, with up to four purchases per month. Unused Moonlight expires at the next weekly reset, when your pass returns to 100%.",
                    ) : uiText(
                        'moonlight.refillConfirmDetail',
                        'Refills your Moonlight bar for the current billing cycle. One-time purchase, nothing renews. Unused Moonlight does not carry over: your pass refills the bar at your next renewal.',
                    )} ${taxNoteText(storeDisplayTax, uiText)}`,
                    confirmLabel: uiText('moonlight.refillConfirmGo', 'Continue to payment'),
                    cancelLabel: uiText('common.cancel', 'Cancel'),
                },
            );
            if (ok) beginCheckout('moonlight_refill');
        });
    }

    /* ONE LISTENER FOR THE WHOLE CONTENT (CSP-safe: no inline onclick).
       The nodes on screen are kept across paints now (reconciled, never
       rebuilt), so a listener bound per button would be bound again on every
       paint and one press would act twice. */
    function handleStoreContentClick(event) {
        const container = event.currentTarget;
        const source = event.target instanceof Element ? event.target : null;
        const period = source?.closest('[data-store-period]');
        if (period && container.contains(period)) {
            selectPassPeriod(container, period.getAttribute('data-store-period'));
            return;
        }
        const button = source?.closest('[data-store-action]');
        if (!button || !container.contains(button)) return;
        const action = button.getAttribute('data-store-action');
        if (action === 'checkout') beginCheckout(button.getAttribute('data-plan'));
        else if (action === 'manage') manageSubscription();
        else if (action === 'retry-status') retrySubscriptionStatus();
    }

    function wireStoreContent(container) {
        if (container.__moonfallStoreContentHandler === handleStoreContentClick) return;
        replaceEventHandler(container, '__moonfallStoreContentHandler', 'click', handleStoreContentClick);
    }

    let lastSubscriptionState = 'pending';
    /* The markup the content was last painted from. A paint that would
       produce the same markup — a re-read that said the same thing — touches
       no node at all. */
    let paintedStoreHtml = null;
    /* True once THIS open has shown its content (not the loading state). */
    let storePresented = false;
    /* What THIS open's own reads said, where it overrides the answer the
       store holds: 'failed' — the read came back empty-handed, and the
       section shows its error exactly as it always did (a failed entitlement
       read never leaves purchase buttons standing on an older answer);
       'pending' — the retry card's read is out, and the section shows its
       status card until it lands. `null` — paint the answer held. */
    let storeOpenToken = 0;
    const freshOpenStatus = () => ({ subscription: null, stardust: null, moonlight: null });
    let storeOpenStatus = freshOpenStatus();
    /* The newest read of each kind that has reported to THIS open: an older
       one landing late (a hung read behind a refresh) says nothing. */
    let storeOpenSeq = { subscription: 0, stardust: 0, moonlight: 0 };
    let storeHoldTimer = null;

    /** The answer a section paints from, or undefined when it has none. */
    function openAnswer(kind) {
        return storeOpenStatus[kind] ? undefined : storeAnswer(kind);
    }

    function selectPassPeriod(container, period) {
        if (!PASS_PERIODS.includes(period) || period === currentPassPeriod()) return;
        passPeriod = period;
        paintStore(container);
    }

    /* The pass the answer describes, and the module state that follows from
       it (the refill price, the gift card, the Moonlight "+" target). */
    function subscriptionViewState() {
        const status = openAnswer('subscription');
        if (status === undefined) {
            storeSubscriptionResolved = false;
            activePassTier = null;
            complimentaryStatus = null;
            activeBillingInterval = null;
            if (storeOpenStatus.subscription === 'failed') return 'error';
            if (!storeOpenStatus.subscription && billingStatusOverride) return billingStatusOverride;
            return 'pending';
        }
        if (status.isPaid) billingStatusOverride = null;
        /* THREE outcomes, not two. `isPaid` is the MANGA question, so a
           Storyteller Pass holder answered false to it and was shown a
           Subscribe button for a plan the server's duplicate-subscription
           guard refuses. They are offered the upgrade instead. */
        const holdsStoryteller = status.hasActiveSubscription === true && !status.isPaid;
        activeBillingInterval = status.subscriptionBillingInterval === 'year'
            || status.subscriptionBillingInterval === 'month'
            ? status.subscriptionBillingInterval
            : null;
        /* FOUR outcomes now: a complimentary pass (a redeemed voucher)
           reads as active for every gate but has no subscription behind
           it, so it gets its own screen rather than a Manage button. */
        complimentaryStatus = status.complimentary === true
            ? {
                tier: status.subscriptionType,
                expiresAt: status.complimentaryExpiresAt,
                giftPolicyVersion: status.complimentaryGiftPolicyVersion || null,
                deferred: status.deferredSubscription || null,
                scheduled: status.scheduledSubscription || null,
            }
            : null;
        if (complimentaryStatus) activeBillingInterval = null;
        storeSubscriptionResolved = true;
        // A paused Manga Pass answers isPaid=false without becoming a
        // Storyteller Pass. Its owner needs the hold explained, not an upgrade.
        if (status.paidFeaturesPaused === true) {
            activePassTier = status.hasActiveSubscription === true
                && (status.subscriptionType === 'moonfall_manga' || status.subscriptionType === 'moonfall_storyteller')
                ? status.subscriptionType : null;
            return 'payment_disputed';
        }
        /* The tier the account plays at — a gift counts as its tier — is
           what the Moonlight "+" is answered by. */
        activePassTier = complimentaryStatus
            ? (complimentaryStatus.tier === 'moonfall_storyteller' || complimentaryStatus.tier === 'moonfall_manga' ? complimentaryStatus.tier : null)
            : (status.isPaid ? 'moonfall_manga' : (holdsStoryteller ? 'moonfall_storyteller' : null));
        if (complimentaryStatus) return 'complimentary';
        if (status.isPaid) return 'subscribed';
        return billingStatusOverride || (holdsStoryteller ? 'storyteller' : 'unsubscribed');
    }

    function stardustViewState() {
        const config = openAnswer('stardust');
        if (config === undefined) {
            return { status: storeOpenStatus.stardust === 'failed' ? 'error' : 'pending', packs: [] };
        }
        try {
            return { status: 'ready', packs: normalizeStardustStoreConfig(config) };
        } catch (_) {
            return { status: 'error', packs: [] };
        }
    }

    /* Classes a store control wears for a moment at runtime (the "+" glow):
       a reconcile keeps them rather than cutting the light short. */
    const RUNTIME_CLASSES = Object.freeze(['is-spotlit']);

    function reconcileStoreNode(current, next) {
        if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
            current.replaceWith(next);
            return;
        }
        if (current.nodeType !== 1) {
            if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
            return;
        }
        for (const name of current.getAttributeNames()) {
            if (!next.hasAttribute(name)) current.removeAttribute(name);
        }
        for (const name of next.getAttributeNames()) {
            let value = next.getAttribute(name);
            if (name === 'class') {
                const kept = RUNTIME_CLASSES.filter((runtime) => (
                    current.classList.contains(runtime) && !next.classList.contains(runtime)
                ));
                if (kept.length) value = `${value} ${kept.join(' ')}`;
            }
            if (current.getAttribute(name) !== value) current.setAttribute(name, value);
        }
        reconcileStoreChildren(current, next);
    }

    function reconcileStoreChildren(parent, nextParent) {
        const incoming = Array.from(nextParent.childNodes);
        incoming.forEach((next, index) => {
            const current = parent.childNodes[index];
            if (current) reconcileStoreNode(current, next);
            else parent.appendChild(next);
        });
        while (parent.childNodes.length > incoming.length) parent.lastChild.remove();
    }

    /**
     * RECONCILE, NEVER REBUILD (docs/asset-loading.md law 2).
     *
     * The content, from what the store knows now, in one pass. The first
     * paint of an open replaces the loading state; every later one is
     * reconciled into the nodes already on screen — a text node takes its new
     * words, an attribute its new value, a picture keeps its decoded pixels, a
     * focused button keeps the focus — and a paint that would produce the
     * same markup touches nothing. The sections used to be rebuilt with
     * `innerHTML` by each read as it landed, in either order, so every read
     * re-created the emblems and moved the other section.
     */
    function paintStoreContent(container) {
        stardustPackState = stardustViewState();
        // The packs are prices too: re-check that the inclusive figures
        // cover them before the cards are painted with either.
        applyDisplayTax();
        const state = subscriptionViewState();
        lastSubscriptionState = state;
        const html = buildStoreContent(state);
        wireStoreContent(container);
        if (container.dataset.storeView !== 'content') {
            container.innerHTML = html;
            container.dataset.storeView = 'content';
            container.removeAttribute('aria-busy');
        } else if (html !== paintedStoreHtml) {
            const next = document.createElement('template');
            next.innerHTML = html;
            reconcileStoreChildren(container, next.content);
        }
        paintedStoreHtml = html;
        watchStoreHeads(container);
    }

    /* ONE LINE AND ONE HEIGHT FOR BOTH SECTION HEADS (2026-10-17).
       The two heads share one subgrid row, so the pass cards and the Stardust
       packs start on one line however the titles wrap. Under 400px of page
       height the pass column lays out rows of its own instead (store.css: its
       Subscribe buttons must stay inside the visible store while the Stardust
       column may run on below the fold), and it stopped seeing the Stardust
       head. Where "Sternenstaub-Guthaben", "Créditos de polvo estelar" or
       "Créditos de poeira estelar" wrapped in the narrower column, the packs
       sat one line, 21px, below the passes.

       Giving the pass head that second line as well was measured too: at
       844x280 behind a notch the pass cards then lost the 21px they needed,
       and the Manga card's badge ran into its title and its Subscribe button
       out of its card. So in that band the two titles share one font size, as
       large as the band gives them but no larger than lets both stay on one
       line (`--store-title-fit`, never under STORE_TITLE_FIT_FLOOR; 0.82 is the
       smallest any shipped language needs). English and every title that
       already fits keep their size. Whatever height is still unequal (a
       title past the floor, the period control beside the pass title) is
       evened out by `--store-head-row`, the taller head's natural height,
       which both heads take as their minimum.

       Measured synchronously when the content is painted (an open, a
       language switch, a reconcile), when the store's own box changes size
       and when a font lands, so a title is never painted at a size it then
       leaves. Nothing measured here depends on what it sets: the store box is
       sized by the modal, and the heads' children are not stretched. */
    const STORE_TITLE_FIT_FLOOR = 0.75;
    let storeHeadObserver = null;
    let storeHeadFontsListener = null;

    function cssPx(style, property) {
        return parseFloat(style[property]) || 0;
    }

    function naturalHeadHeight(head) {
        // Element boxes, not a Range over the text: glyph boxes stand taller
        // than the title's line box and would grow every head by ~5px.
        let top = Infinity;
        let bottom = -Infinity;
        for (const child of head.children) {
            const rect = child.getBoundingClientRect();
            if (!rect.width && !rect.height) continue;
            const style = getComputedStyle(child);
            top = Math.min(top, rect.top - cssPx(style, 'marginTop'));
            bottom = Math.max(bottom, rect.bottom + cssPx(style, 'marginBottom'));
        }
        if (bottom < top) return 0;
        const style = getComputedStyle(head);
        return bottom - top + cssPx(style, 'paddingTop') + cssPx(style, 'paddingBottom');
    }

    /* The width a title has on its line: its head's, less whatever shares
       the line with it (the period control beside the pass title). */
    function titleRoom(title) {
        const head = title.parentElement;
        const style = getComputedStyle(head);
        let room = head.clientWidth - cssPx(style, 'paddingLeft') - cssPx(style, 'paddingRight');
        for (const sibling of head.children) {
            if (sibling === title) continue;
            const rect = sibling.getBoundingClientRect();
            if (!rect.width && !rect.height) continue;
            room -= rect.width + cssPx(style, 'columnGap');
        }
        return room;
    }

    /* The title on ONE line at the band's full size, read from an unseen copy
       so the title on screen never changes while it is measured. The copy is
       set beside the content rather than in it: the content is reconciled,
       never rebuilt, and a repaint that changes nothing must touch no node in
       it (storeOpensComplete.test.js). It also sets the fit back to 1 for
       itself: dividing a width read at the current fit by that fit drifts by
       a hundredth of a pixel each time (text does not scale exactly
       linearly), and every repaint would nudge the size. */
    function oneLineWidth(title) {
        const host = title.closest('.store-container') || document.body;
        const ruler = title.cloneNode(true);
        ruler.setAttribute('aria-hidden', 'true');
        ruler.style.cssText = 'position:absolute;left:0;top:0;visibility:hidden;pointer-events:none;white-space:nowrap;width:auto;max-width:none;--store-title-fit:1;';
        host.appendChild(ruler);
        const width = ruler.getBoundingClientRect().width;
        ruler.remove();
        return width;
    }

    function setStoreVar(container, name, value) {
        if (container.style.getPropertyValue(name) === value) return;
        if (value) container.style.setProperty(name, value);
        else container.style.removeProperty(name);
    }

    function fitStoreHeads(container) {
        const titles = container.querySelectorAll('.store-section__head > .store-section__title');
        if (titles.length < 2) {
            setStoreVar(container, '--store-title-fit', '');
            setStoreVar(container, '--store-head-row', '');
            return;
        }
        let fit = 1;
        titles.forEach((title) => {
            const width = oneLineWidth(title);
            const room = titleRoom(title);
            // A pixel of slack: a line exactly as wide as its box may wrap
            // on a sub-pixel rounding.
            if (width > 0 && room > 0) fit = Math.min(fit, (room - 1) / width);
        });
        fit = Math.max(STORE_TITLE_FIT_FLOOR, Math.min(1, Math.floor(fit * 1000) / 1000));
        setStoreVar(container, '--store-title-fit', fit < 1 ? String(fit) : '');
        const heads = container.querySelectorAll('.store-section__head');
        setStoreVar(container, '--store-head-row', `${Math.max(...Array.from(heads, naturalHeadHeight)).toFixed(2)}px`);
    }

    function watchStoreHeads(container) {
        fitStoreHeads(container);
        if (!storeHeadObserver && typeof ResizeObserver === 'function') {
            storeHeadObserver = new ResizeObserver(() => fitStoreHeads(container));
            storeHeadObserver.observe(container);
        }
        if (!storeHeadFontsListener && document.fonts?.addEventListener) {
            storeHeadFontsListener = () => fitStoreHeads(container);
            document.fonts.addEventListener('loadingdone', storeHeadFontsListener);
        }
    }

    function unwatchStoreHeads() {
        storeHeadObserver?.disconnect();
        storeHeadObserver = null;
        if (storeHeadFontsListener) {
            document.fonts?.removeEventListener?.('loadingdone', storeHeadFontsListener);
            storeHeadFontsListener = null;
        }
    }

    /** Everything the store shows, from what it knows now. */
    function paintStore(container) {
        paintStoreContent(container);
        paintStoreMoonlight();
        // The refill, and any "+" that was waiting for these answers.
        applyRefillVisibility();
    }

    function clearStoreHold() {
        if (storeHoldTimer) {
            clearTimeout(storeHoldTimer);
            storeHoldTimer = null;
        }
    }

    function storeReadyToPresent() {
        return storeArtSettled && STORE_READ_KINDS.every((kind) => (
            openAnswer(kind) !== undefined || storeOpenStatus[kind] === 'failed'
        ));
    }

    function presentStore(container) {
        clearStoreHold();
        storePresented = true;
        paintStore(container);
    }

    /* ONE ORGANIZED LOADING STATE, for a store opened before its answers are
       in (a press in the first moments of a cold boot, before the lobby's
       warm has landed). It stands for the WHOLE content: the sections are not
       painted one by one as their reads land, and they are not painted around
       spinners — the finished store replaces it in one frame once the three
       reads and the five pictures are in, or at `STORE_OPEN_HOLD_MS`. It is
       also unseen for the shared loading grace (store.css), so an answer that
       is a moment away never flashes it at all. */
    function showStoreLoading(container) {
        container.dataset.storeView = 'loading';
        paintedStoreHtml = null;
        /* No pass is on screen: a "+" pressed now waits for the content
           rather than settling on what the last open (or account) showed. */
        lastSubscriptionState = 'pending';
        container.setAttribute('aria-busy', 'true');
        container.innerHTML = `
            <div class="store-loading" role="status" aria-live="polite" data-loading="true">
                <span class="store-status-spinner" aria-hidden="true"></span>
                <p class="store-status-copy">${htmlText('store.loadingOffers', 'Loading Moonfall offers...')}</p>
            </div>
        `;
    }

    /* A read (or the pictures) came in: painted into the store on screen,
       or — behind the loading state — the finished store presented once the
       last of them is in. */
    function onStoreDataSettled() {
        if (!isOpen) return;
        const container = document.getElementById('store-content');
        if (!container) return;
        if (storePresented) paintStore(container);
        else if (storeReadyToPresent()) presentStore(container);
    }

    function repaintStore() {
        onStoreDataSettled();
    }

    /** The waiter one of THIS open's reads reports to. */
    function openReadSettled(kind) {
        const token = storeOpenToken;
        return (ok, seq) => {
            if (token !== storeOpenToken || seq < storeOpenSeq[kind]) return;
            storeOpenSeq[kind] = seq;
            storeOpenStatus[kind] = ok ? null : 'failed';
            onStoreDataSettled();
        };
    }

    /* The retry card's button: the pass section asks again, in place. */
    function retrySubscriptionStatus() {
        const container = document.getElementById('store-content');
        if (!container || !isOpen) return;
        storeOpenStatus.subscription = 'pending';
        paintStore(container);
        readStore('subscription', { origin: 'refresh', onSettled: openReadSettled('subscription') });
    }

    function withSubscriptionStatusTimeout(statusPromise) {
        return new Promise((resolve, reject) => {
            let settled = false;
            const finish = (callback, value) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                callback(value);
            };
            const timeoutId = setTimeout(() => {
                const error = new Error('Subscription status request timed out');
                error.code = 'subscription_status_timeout';
                finish(reject, error);
            }, SUBSCRIPTION_STATUS_TIMEOUT_MS);

            Promise.resolve(statusPromise).then(
                (status) => finish(resolve, status),
                (error) => finish(reject, error)
            );
        });
    }

    /* The store is priced in Moonlight, so it shows Moonlight — as the same
       segments and the same percentage the top strip shows, painted by the
       same renderer so the two can never disagree.

       NO WORDS. It carried the duel counts as well and the owner's verdict was
       blunt: the counts belong on the hover card, and a pill with a sentence
       in it beside a store full of copy is noise.

       NO BLANK FRAME. Every re-read used to paint the meter UNKNOWN first — an
       em-dash, dimmed, in the free tier's silver — and the value again a beat
       later. A return from checkout re-reads five times in a minute, so the
       owner watched the bar "keep resetting". It is now painted from the
       answer the store holds, with the rest of the store, and only when that
       answer changed; a re-read repaints in place, and only a read that
       actually fails shows unknown. */
    let paintedMoonlightKey;
    function paintStoreMoonlight() {
        const answer = openAnswer('moonlight');
        const resolved = answer !== undefined || storeOpenStatus.moonlight === 'failed';
        /* Before this account's first answer, the lobby chip's value when it
           has one — the same renderer, so the two agree from the store's first
           frame — and the honest unknown when it has none. It is only shown:
           the refill and the "+" wait for the store's own answer
           (`storeMoonlightResolved`). */
        storeMoonlightStatus = knownMoonlight(resolved ? answer : peekMoonlightStatus());
        storeMoonlightResolved = resolved;
        const meter = document.getElementById('store-moonlight');
        if (!meter) return;
        const key = JSON.stringify(storeMoonlightStatus);
        if (key === paintedMoonlightKey) return;
        paintedMoonlightKey = key;
        renderMoonlightChip(meter, storeMoonlightStatus);
    }

    function knownMoonlight(status) {
        return status?.state === 'known' ? status : null;
    }

    /** Take the Moonlight again (a purchase or a grant moved it). */
    function renderStoreMoonlight() {
        refreshStoreRead('moonlight', openReadSettled('moonlight'));
    }

    /* SHOWN EXACTLY WHEN THE SERVER SAYS A REFILL MAY START (the owner,
       2026-09-24: "only work and only appear if the moonlight is very low,
       like below 10%"). `refillAvailable` is decided with the tier, so only a
       Manga Pass, paid or gifted, ever sees one (2026-09-25). Two reads decide
       it and they land in either order; until both are in the button keeps
       what it showed, so a re-read never blinks it out and back — except
       that a subscription read that FAILED leaves no price to put on a
       refill, so that open offers none. Every paint of the store re-applies
       the rule, and writes only what differs. */
    function applyRefillVisibility() {
        const refill = document.getElementById('store-moonlight-refill');
        if (!refill) return;
        if (storeSubscriptionResolved && storeMoonlightResolved) paintRefill(refill);
        else if (storeOpenStatus.subscription === 'failed' && !refill.hidden) refill.hidden = true;
        /* A MOONLIGHT "+" MAY BE WAITING FOR THIS: the refill is the Manga
           holder's target, and it is only shown once this read is in. */
        settlePendingSpotlight();
        wireStoreBalancePluses();
    }

    function paintRefill(refill) {
        const status = storeMoonlightStatus;
        const refillable = status?.refillAvailable === true;
        if (refill.hidden !== !refillable) refill.hidden = !refillable;
        /* "+ Refill": the "+" is drawn by store.css, the verb is the short
           translation (the full "Refill Moonlight" ran to 172px in French and
           crowded the title; a bare "+" made the owner guess). The full
           sentence is what a screen reader announces and what the tooltip
           shows; the dialog behind it states the one-time terms before any
           payment. */
        const refillLabel = uiText('moonlight.refillNow', 'Refill Moonlight');
        // Match the amount checkout charges for this holder's cadence.
        const priceMinor = refillable
            ? refillPriceMinor({ subscriptionType: status.subscriptionType, interval: activeBillingInterval, weeklyReset: status.weeklyReset === true })
            : null;
        /* The same figure the checkout charges — which, where tax is shown
           inclusively, is the total rather than the seller's net. */
        const price = Number.isSafeInteger(priceMinor) && priceMinor > 0
            ? usd(displayTotalMinor(priceMinor, storeDisplayTax))
            : '';
        // Written only when it changes: a re-read that decides the same
        // thing leaves the button's nodes exactly as they were.
        const text = price
            ? `${uiText('moonlight.refillShort', 'Refill')} · ${price}`
            : uiText('moonlight.refillShort', 'Refill');
        if (refill.textContent !== text) refill.textContent = text;
        setAttributeIfChanged(refill, 'aria-label', price ? `${refillLabel} · ${price}` : refillLabel);
        setAttributeIfChanged(refill, 'title', `${refillLabel} \u2014 ${refillOnceText(status)}`);
    }

    function weeklyStorePolicy() {
        const subscription = openAnswer('subscription');
        const moonlight = openAnswer('moonlight');
        return subscription?.weeklyMoonlightPurchases === true
            || subscription?.weeklyReset === true || moonlight?.weeklyReset === true;
    }

    function refillOnceText(status) {
        return status?.weeklyReset === true
            ? uiText('moonlight.refillOnceWeekly', 'One-time purchase. Unused refill Moonlight expires at the next weekly reset. Up to four purchases per month.')
            : uiText('moonlight.refillOnce', 'One-time purchase. Your pass refills the bar at your next renewal regardless.');
    }

    /* A new pass or refill releases its Moonlight in parts
       (withdrawalRelease.js); a low bar's refill waits until all of it has
       arrived. The date the next part arrives, or '' when the refill waits
       for nothing. */
    function refillWaitsForReleaseDate(status) {
        const releaseAt = status?.refillWaitsForRelease === true ? Number(status?.nextReleaseAt) : NaN;
        if (!Number.isFinite(releaseAt) || releaseAt <= 0) return '';
        try {
            return new Date(releaseAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
        } catch (_) { return ''; }
    }

    /* When a refill opens, in the player's language. The line comes from the
       server with the decision, so there is no second copy of the rule. */
    function refillWhenLowText(status) {
        /* ON HOLD FOR A DISPUTED PAYMENT (2026-09-28): the account plays free
           until the bank decides, so the "+" explains that, not a refill. */
        if (status?.paymentDisputeOnHold === true) {
            return accountOnHoldCopy(status, uiText).line;
        }
        /* ONE REFILL PER BILLING PERIOD (the owner, 2026-09-28): this period's
           is bought, so say when the next period opens refills again rather
           than "a refill opens below 10%". */
        if (status?.refillUsedThisCycle === true) {
            let date = '';
            const opensAt = Number(status?.refillsOpenAt);
            if (Number.isFinite(opensAt) && opensAt > 0) {
                try {
                    date = new Date(opensAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
                } catch (_) { date = ''; }
            }
            return uiText(
                status?.weeklyReset === true ? 'moonlight.refillUsedThisMonth' : 'moonlight.refillUsedThisPeriod',
                status?.weeklyReset === true
                    ? "You've used all four refills this month. Refills open again {{date}}. Weekly resets do not reset this limit."
                    : "You've used this billing period's refill. Refills open again when your next billing period starts ({{date}}).",
                { date },
            );
        }
        const date = refillWaitsForReleaseDate(status);
        if (date) {
            return uiText(
                'moonlight.refillAfterRelease',
                'More of your Moonlight arrives {{date}}. A refill opens once all of it has arrived.',
                { date },
            );
        }
        const line = Number(status?.refillBelowPercent);
        if (!Number.isFinite(line) || line <= 0) return '';
        return uiText('moonlight.refillWhenLow', 'A refill opens when your Moonlight falls below {{n}}%.', { n: line });
    }

    /** The lit class comes off at the animation's end — or after its length
        when no animation runs (reduced motion), so a light never sticks. */
    function lightOnce(node, ms = 1800) {
        node.classList.remove('is-spotlit');
        void node.offsetWidth;
        node.classList.add('is-spotlit');
        const clear = () => node.classList.remove('is-spotlit');
        node.addEventListener('animationend', clear, { once: true });
        setTimeout(clear, ms);
    }

    /** One glow, then focus, on any store control. */
    function spotlightControl(node) {
        if (!node) return false;
        lightOnce(node);
        if (typeof node.focus === 'function') node.focus({ preventScroll: true });
        return true;
    }

    /* The Moonlight card keeps its own English for a key that comes back
       unresolved; defaulting to the key here printed it. */
    function translateForCard(key, replacements) {
        return i18n?.t?.(key, replacements);
    }

    /**
     * THE MOONLIGHT "+" — the lobby chip's, or the store's own pill and its
     * "+" — leads to MORE MOONLIGHT, by what the account holds (the owner,
     * 2026-09-21, after the old fallback lit one Subscribe button):
     *   - no pass: the pass cards themselves, both lit;
     *   - a Storyteller Pass: the Manga Pass card — the tier above, the
     *     upgrade;
     *   - a Manga Pass (the top): the Refill beside the meter, lit, focused
     *     and — on a press in the store — explained by the Moonlight card
     *     with the refill's terms under the duel counts.
     * The reads that decide this land in either order, so the "+" is kept
     * pending until they have, and every arrival calls back here.
     */
    function settlePendingSpotlight() {
        if (!isOpen || !pendingSpotlight) return false;
        if (pendingSpotlight === 'stardust') {
            if (!document.querySelector('#store-content .store-section--stardust .plan-btn[data-store-action="checkout"]')) return false;
            pendingSpotlight = null;
            return spotlightStardust();
        }
        if (lastSubscriptionState === 'pending') return false;
        const explain = pendingSpotlightExplain;
        if (lastSubscriptionState === 'payment_disputed') {
            if (!storeMoonlightResolved) return false;
            pendingSpotlight = null;
            pendingSpotlightExplain = false;
            const meter = document.getElementById('store-moonlight-balance');
            if (explain && meter && storeMoonlightStatus?.paymentDisputeOnHold === true) {
                openMoonlightCard(meter, storeMoonlightStatus, {
                    t: translateForCard,
                    note: refillWhenLowText(storeMoonlightStatus),
                });
            }
            return true;
        }
        const target = moonlightPlusTarget(activePassTier);
        if (target.kind === 'refill') {
            /* The refill is decided by the store's own Moonlight answer —
               until one is held (the meter may be showing the lobby chip's
               value) the button may still show what the last open decided —
               so the "+" waits for it before it lands anywhere. */
            if (!storeMoonlightResolved) return false;
            const refill = document.getElementById('store-moonlight-refill');
            if (refill && !refill.hidden) {
                pendingSpotlight = null;
                pendingSpotlightExplain = false;
                spotlightControl(refill);
                if (explain) {
                    const price = refill.textContent.trim();
                    openMoonlightCard(refill, storeMoonlightStatus, {
                        t: translateForCard,
                        note: `${price} — ${refillOnceText(storeMoonlightStatus)}`,
                    });
                }
                return true;
            }
            /* NOT A LOW BAR: there is nothing to buy yet, so the store's own
               "+" says when there will be — the Moonlight card, under the
               meter, with the line beneath the counts. Lighting the pass the
               player already holds answered a question nobody asked. A read
               that failed leaves nothing to explain; that falls to the pass
               card.

               ONLY THE STORE'S OWN PRESS EXPLAINS (owner, 2026-09-25). The
               lobby chip opens the store with this same lead, and the card
               then sat over the store with its duel counts until the player
               clicked it away: "inside the moon store, it should no longer
               have that estimation of the number of duels". Arriving from the
               chip lands in the store and lights nothing. */
            if (storeMoonlightStatus?.refillAvailable === false && !explain) {
                pendingSpotlight = null;
                pendingSpotlightExplain = false;
                return true;
            }
            const whenLow = storeMoonlightStatus?.refillAvailable === false
                ? refillWhenLowText(storeMoonlightStatus)
                : '';
            const meter = document.getElementById('store-moonlight-balance');
            if (whenLow && meter) {
                pendingSpotlight = null;
                pendingSpotlightExplain = false;
                openMoonlightCard(meter, storeMoonlightStatus, {
                    t: translateForCard,
                    note: whenLow,
                    // The note then says when the next part arrives; the card need not.
                    noteSaysWhen: Boolean(refillWaitsForReleaseDate(storeMoonlightStatus)),
                });
                return true;
            }
        }
        pendingSpotlight = null;
        pendingSpotlightExplain = false;
        return spotlightPassCards(target.kind === 'upgrade' ? target.tier : (target.kind === 'refill' ? activePassTier : null));
    }

    /**
     * The pass cards, scrolled to and lit — one tier's card, or every card
     * in the section (the status card included, when that is what the
     * section holds). Focus goes to the first lit card's own button, which
     * a pointer press leaves unmarked (`:focus-visible`) and a keyboard
     * shows.
     * @param {string | null} tier
     */
    function spotlightPassCards(tier) {
        const passes = /** @type {HTMLElement | null} */ (document.querySelector('#store-content .store-section--pass'));
        if (!passes) return false;
        const all = Array.from(passes.querySelectorAll('.plan-card'));
        const cards = tier ? all.filter((card) => card.getAttribute('data-pass-tier') === tier) : all;
        const lit = cards.length ? cards : all;
        if (!lit.length) return false;
        passes.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
        lit.forEach((card) => lightOnce(card, 2000));
        const button = /** @type {HTMLElement | null} */ (lit[0].querySelector('button[data-store-action]'));
        button?.focus({ preventScroll: true });
        return true;
    }

    /** The store's own Moonlight "+" (and the pill it sits in). */
    function leadFromMoonlightPlus() {
        pendingSpotlight = 'refill';
        pendingSpotlightExplain = true;
        return settlePendingSpotlight();
    }

    /** THE STARDUST "+": the Stardust packs, scrolled to, lit, focused. */
    function spotlightStardust() {
        const section = /** @type {HTMLElement | null} */ (document.querySelector('#store-content .store-section--stardust'));
        if (!section) return false;
        section.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
        lightOnce(section, 2000);
        const first = /** @type {HTMLElement | null} */ (section.querySelector('.plan-btn[data-store-action="checkout"]'));
        if (first) first.focus({ preventScroll: true });
        return true;
    }

    /**
     * The pluses inside the store's own pills, and the meter's hover card:
     * the same duel estimate the lobby chip shows, since a store is where a
     * player weighs what their Moonlight buys.
     */
    function wireStoreBalancePluses() {
        if (storeBalancePlusesWired) return;
        const stardustPlus = document.getElementById('store-stardust-plus');
        const moonlightPlus = document.getElementById('store-moonlight-plus');
        const stardustPill = document.getElementById('store-stardust-balance');
        const meter = document.getElementById('store-moonlight-balance');
        if (!stardustPlus || !moonlightPlus || !stardustPill || !meter) return;
        storeBalancePlusesWired = true;
        /* THE WHOLE PILL IS THE "+" (the owner, 2026-09-21): a press anywhere
           on the Stardust pill or the meter does what its "+" does, as the
           lobby's chips already do. One listener on the pill, so a press on
           the "+" itself is not counted twice. */
        /* THE "+" GETS ITS OWN BEAT HERE TOO (the owner, 2026-09-21: "the
           visual effect of pressing the sign should happen on all screens
           including moon store"). On a chip the "+" is a drawn `::after` and
           `isPlusPress` measures it; in the store it is a real button, so the
           press is simply the one that landed on it. */
        const plusPressed = (event) => {
            const node = event.target instanceof Element ? event.target.closest('.store-balance-plus') : null;
            if (node instanceof HTMLElement) pulsePlus(node);
        };
        stardustPill.addEventListener('click', (event) => {
            event.preventDefault();
            plusPressed(event);
            closeMoonlightCard();
            spotlightStardust();
        });
        meter.addEventListener('click', (event) => {
            event.preventDefault();
            plusPressed(event);
            closeMoonlightCard();
            leadFromMoonlightPlus();
        });
        const showCard = () => {
            if (!isOpen || !storeMoonlightStatus) return;
            openMoonlightCard(meter, storeMoonlightStatus, { t: translateForCard });
        };
        meter.addEventListener('mouseenter', showCard);
        meter.addEventListener('mouseleave', closeMoonlightCard);
        moonlightPlus.addEventListener('focus', onKeyboardFocus(moonlightPlus, showCard));
        moonlightPlus.addEventListener('blur', closeMoonlightCard);
    }

    /* "Withdraw from contract here" in the footer (Consumer Rights Directive
       Art. 11a): delegated once on the content root, which is re-rendered. */
    function wireStoreWithdrawLink(container) {
        if (!container || container.dataset.withdrawWired === '1') return;
        container.dataset.withdrawWired = '1';
        container.addEventListener('click', (event) => {
            const trigger = event.target instanceof Element ? event.target.closest('[data-store-withdraw]') : null;
            if (!trigger) return;
            event.preventDefault();
            void WithdrawalPanel.open();
        });
    }

    /**
     * An answer a door's open takes as its own, without asking again: only
     * the Stardust pack prices, a public config no purchase moves, and only
     * while fresh, not failed in this open and with no read of them out. The
     * pass and the Moonlight are the account's, and every open asks for them.
     * @param {StoreReadKind} kind
     */
    function storeOpenAdopts(kind) {
        return kind === 'stardust'
            && storeOpenStatus[kind] !== 'failed'
            && !storeReads[kind].inflight
            && storeReadFresh(kind);
    }

    /**
     * The store's content for the open store: asks for its answers again and
     * paints what it knows. `reread: true` asks for all three (something
     * changed them); `'open'` is a door's open, which asks for the pass and
     * the Moonlight and keeps fresh pack prices (`storeOpenAdopts`); `false`
     * is only new copy.
     *
     * An open whose answers are all in — warmed by the lobby or on intent —
     * is painted whole before its first frame, and its re-reads are
     * reconciled in when they land. An open that is missing any of them (or
     * whose pictures have not decoded) shows ONE loading state instead, and
     * the finished store replaces it when the last one lands, capped at
     * `STORE_OPEN_HOLD_MS`. A store already on screen is never taken back to
     * the loading state: a re-read after a purchase or a voucher updates the
     * store in place.
     * @param {{ reread?: boolean | 'open' }} [options]
     */
    function renderPlans({ reread = true } = {}) {
        const container = document.getElementById('store-content');
        if (!container) return;
        wireStoreWithdrawLink(container);
        wireStoreRefill();
        /* The pills answer from the first frame. Wired only once a read had
           landed, a "+" pressed in the moment after opening did nothing — and
           a store opened from the Stardust chip then lit the packs instead.
           A press before the reads is kept (`pendingSpotlight`) and answered
           when they land. */
        wireStoreBalancePluses();
        ensureStoreReadOwner();
        if (reread) {
            /* A store already on screen keeps what its last reads said until
               the new ones answer: a meter showing its unknown, or a pass
               section showing its retry card, is not swapped for the older
               answer and back while the re-read is out. */
            const shown = isOpen && storePresented ? storeOpenStatus : null;
            storeOpenToken += 1;
            storeOpenStatus = freshOpenStatus();
            if (shown) {
                for (const kind of STORE_READ_KINDS) {
                    if (shown[kind] === 'failed') storeOpenStatus[kind] = 'failed';
                }
            }
            storeOpenSeq = { subscription: 0, stardust: 0, moonlight: 0 };
            for (const kind of STORE_READ_KINDS) {
                if (reread === 'open' && storeOpenAdopts(kind)) continue;
                readStore(kind, { origin: 'open', onSettled: openReadSettled(kind) });
            }
        }
        // Closed (a checkout answered after the store was shut): the answers
        // are refreshed for the next open, and nothing is painted.
        if (!isOpen) return;
        if (storePresented || storeReadyToPresent()) {
            presentStore(container);
            return;
        }
        showStoreLoading(container);
        /* The meter is outside the content and in the first frame too: the
           answer the store holds, or the lobby chip's value. */
        paintStoreMoonlight();
        if (storeHoldTimer) return;
        storeHoldTimer = setTimeout(() => {
            storeHoldTimer = null;
            if (isOpen && !storePresented) presentStore(container);
        }, STORE_OPEN_HOLD_MS);
    }

    function normalizeCheckoutUserId(value) {
        const userId = String(value || '').trim();
        return userId && userId.length <= 128 ? userId : null;
    }

    function getCheckoutReturnSurface() {
        const opener = storeReturnFocus;
        if (!opener?.isConnected || typeof opener.closest !== 'function') return null;

        if (opener.closest('#collection-modal') && Collection?.isOpen?.()) {
            const section = Collection.getActiveSection?.();
            return COLLECTION_SECTIONS.has(section)
                ? { surface: 'collection', section }
                : null;
        }

        if (
            opener.closest('[data-foundry-surface-root]')
            && FoundrySurface?.isOpen?.()
        ) {
            const activeTab = FoundryLifecycle?.getActiveTab?.();
            let tab = null;
            if (activeTab === 'moon-dream-panel') tab = 'moon-dream';
            else if (activeTab === 'moon-fuse-panel') tab = 'moon-fuse';
            if (!tab) return null;
            return {
                surface: 'foundry',
                tab,
            };
        }

        return null;
    }

    function createCheckoutReturnHint() {
        const userId = normalizeCheckoutUserId(Firebase?.getUser?.()?.uid);
        const destination = getCheckoutReturnSurface();
        return userId && destination
            ? { version: 1, uid: userId, createdAt: Date.now(), ...destination }
            : null;
    }

    function rememberCheckoutReturn(hint) {
        try {
            window.sessionStorage?.removeItem(CHECKOUT_RETURN_STORAGE_KEY);
            if (!hint) return false;
            window.sessionStorage?.setItem(CHECKOUT_RETURN_STORAGE_KEY, JSON.stringify(hint));
            return true;
        } catch (_) {
            return false;
        }
    }

    function normalizeCheckoutReturnHint(raw, expectedUserId) {
        let hint;
        try {
            hint = JSON.parse(raw);
        } catch (_) {
            return null;
        }
        if (!hint || typeof hint !== 'object' || Array.isArray(hint) || hint.version !== 1) {
            return null;
        }

        const userId = normalizeCheckoutUserId(hint.uid);
        const createdAt = Number(hint.createdAt);
        const now = Date.now();
        if (
            !userId
            || userId !== normalizeCheckoutUserId(expectedUserId)
            || !Number.isSafeInteger(createdAt)
            || createdAt > now + CHECKOUT_RETURN_FUTURE_TOLERANCE_MS
            || now - createdAt > CHECKOUT_RETURN_TTL_MS
        ) {
            return null;
        }

        if (hint.surface === 'collection' && COLLECTION_SECTIONS.has(hint.section)) {
            return { surface: 'collection', section: hint.section };
        }
        if (hint.surface === 'foundry' && FOUNDRY_TABS.has(hint.tab)) {
            return { surface: 'foundry', tab: hint.tab };
        }
        return null;
    }

    /**
     * THE MOON PASS CARDBACK IS GRANTED BY A WEBHOOK, NOT BY THE RETURN TRIP.
     *
     * Stripe sends the player back through a full boot, and `main.js` drains
     * the achievement queue there — but Stripe's webhook regularly commits the
     * subscription AFTER the browser is already home, so that drain finds
     * nothing and the Manga Reader cardback waits for the next launch.
     *
     * A bounded second look closes the race: a few attempts, only after a
     * completed checkout, stopping the moment a banner is actually shown. It
     * is deliberately not an interval — the fallback for a very late webhook
     * remains the ordinary next-launch drain.
     *
     * The one-time purchases (Stardust, a refill) no longer wait for the
     * webhook at all — `confirmCheckoutReturn` credits them on arrival — so
     * these passes are the net under it. They reach a minute because the
     * webhook does: measured on production 2026-09-19, Stripe delivered it
     * 36 s after the payment, and the old 3 s / 12 s pair had both fired.
     */
    const LATE_WEBHOOK_LOOK_DELAYS_MS = [3_000, 12_000, 30_000, 60_000];

    /* THE METER IS STALE ON RETURN FROM CHECKOUT, and a redirect cannot fix it.
       Stripe sends the player back the moment payment clears, but the grant
       lands via the WEBHOOK, which is asynchronous — so the balance read on
       arrival is usually taken before the entitlement exists. The top-strip
       chip then keeps that value for the whole session: `mountMoonlightChip`
       re-reads when an identity lands, and deliberately not afterwards,
       because a refreshed token is not new information about a known value. A
       purchase IS new information, so it needs its own signal.

       The Stardust chip is the same kind of thing (`mountStardustChip`:
       painted at sign-in, re-read only on `moonfall:stardust-changed`), and it
       was never told about a checkout at all — a bought pack showed the old
       figure until a reload. An announcement without a figure asks it to
       re-read the profile. */
    function refreshMoonlightAfterCheckout() {
        renderStoreMoonlight();
        try {
            window.dispatchEvent(new CustomEvent('moonfall:moonlight-stale'));
        } catch (_) { /* a meter that cannot refresh must never break the return */ }
        announceStardust();
    }

    /* Bounded: the confirm is worth a moment before the pre-checkout surface
       comes back, not a hostage. Past this the surface restores and a late
       answer still announces to the chip. */
    const CHECKOUT_CONFIRM_WAIT_MS = 5_000;

    /* FULFILMENT ON THE RETURN TRIP.

       A Stardust pack or a refill is credited by the webhook, which Stripe
       delivers whenever it likes — 36 s after the payment on production
       2026-09-19 — while the player is already back and looking at the old
       number. The success URL now carries the Checkout Session id, and the
       server confirms it on arrival: reads the session back from Stripe,
       checks it is paid and ours, and records it under the same key the
       webhook uses, so the balance is right within a second of landing and
       the webhook, when it comes, is a dedupe.

       Resolves once the server has answered, definitively or not; it never
       throws. A missing id (a subscription return, an older link) confirms
       nothing — the webhook path is unchanged. */
    async function confirmCheckoutReturn(sessionId) {
        if (typeof sessionId !== 'string' || !sessionId) return { confirmed: false };
        try {
            const idToken = await Firebase.getIdToken();
            if (!idToken) return { confirmed: false };
            const response = await fetch('/api/checkout/confirm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({ sessionId }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data?.fulfilled !== true) {
                // Not an error to the player: nothing was lost, the webhook
                // still credits it, and the late looks below will show it.
                console.warn('[Store] Checkout not confirmed on return:', data?.code || response.status);
                return { confirmed: false };
            }
            // The figure when the confirm itself credited; a re-read when the
            // webhook got there first (the mutation then returns no balance).
            announceStardust(data.stardust);
            if (data.planType === 'moonlight_refill') refreshMoonlightAfterCheckout();
            return { confirmed: true, duplicate: data.duplicate === true, planType: data.planType };
        } catch (error) {
            console.warn('[Store] Checkout confirm failed on return:', error);
            return { confirmed: false };
        }
    }

    function lookAgainForCheckoutUnlocks() {
        let index = 0;
        const look = () => {
            // The balances are re-read on every pass, whether or not an unlock
            // banner is waiting: a subscription changes the meter even when it
            // celebrates nothing, and a pack celebrates nothing at all.
            refreshMoonlightAfterCheckout();
            const consumer = getCloudAchievementBannerConsumer();
            // The passes stop only when a banner was actually shown; a missing
            // or failing drain is no reason to stop re-reading the balances.
            const drained = typeof consumer === 'function'
                ? Promise.resolve().then(() => consumer()).catch(() => 0)
                : Promise.resolve(0);
            drained.then((shown) => {
                if (shown > 0) return; // the grant arrived and was celebrated
                index += 1;
                if (index < LATE_WEBHOOK_LOOK_DELAYS_MS.length) {
                    setTimeout(look, LATE_WEBHOOK_LOOK_DELAYS_MS[index]);
                }
            });
        };
        setTimeout(look, LATE_WEBHOOK_LOOK_DELAYS_MS[0]);
    }

    /* The boot veil's hold for a checkout return: the confirm wait plus the
       restored surface's presentation, under the veil's own backstop. */
    const CHECKOUT_RETURN_HOLD_MS = CHECKOUT_CONFIRM_WAIT_MS + 3_000;

    /**
     * Whether this boot returns from a checkout to a surface it will restore:
     * the payment answer in the URL and a valid hint for this account. Reads
     * only — restoreCheckoutReturn consumes the hint.
     */
    function hasPendingCheckoutReturn(userId) {
        try {
            const paymentStatus = new URLSearchParams(window.location.search).get('payment');
            if (paymentStatus !== 'success' && paymentStatus !== 'cancel') return false;
            const raw = window.sessionStorage?.getItem(CHECKOUT_RETURN_STORAGE_KEY);
            return Boolean(raw && normalizeCheckoutReturnHint(raw, userId));
        } catch (_) {
            return false;
        }
    }

    async function restoreCheckoutReturn(userId) {
        const returnParams = new URLSearchParams(window.location.search);
        const paymentStatus = returnParams.get('payment');
        if (paymentStatus !== 'success' && paymentStatus !== 'cancel') return false;
        // Before the stored-hint check below: a checkout started from the
        // lobby has no surface to restore and would otherwise return early
        // with its unlock still unseen.
        if (paymentStatus === 'success') {
            lookAgainForCheckoutUnlocks();
            /* BEFORE the surface comes back: the Collection and the Foundry
               read the balance themselves as they open, so the credit has to
               be in first or their header prints the stale figure the lobby
               chip just stopped showing. */
            await Promise.race([
                confirmCheckoutReturn(returnParams.get('session_id')),
                new Promise((resolve) => setTimeout(resolve, CHECKOUT_CONFIRM_WAIT_MS)),
            ]);
        }

        let raw;
        try {
            raw = window.sessionStorage?.getItem(CHECKOUT_RETURN_STORAGE_KEY);
            if (!raw) return false;
            // Consume before opening. A failed surface cannot loop on reload, and
            // no checkout-only state or controller survives this one-shot handoff.
            window.sessionStorage?.removeItem(CHECKOUT_RETURN_STORAGE_KEY);
        } catch (_) {
            return false;
        }

        const destination = normalizeCheckoutReturnHint(raw, userId);
        if (!destination) return false;

        try {
            if (destination.surface === 'collection') {
                const openCollection = Collection?.open;
                if (typeof openCollection !== 'function') return false;
                await openCollection.call(Collection, destination.section);
            } else {
                const openFoundry = FoundrySurface?.open;
                if (typeof openFoundry !== 'function') return false;
                openFoundry.call(FoundrySurface, { tab: destination.tab });
                // Settles on presentation, as the Collection's open does, so
                // the boot veil lifts onto the Foundry and not before it.
                await FoundrySurface?.whenPresented?.();
            }
            return true;
        } catch (error) {
            console.warn('[Store] Unable to restore the pre-checkout surface:', error);
            return false;
        }
    }

    async function checkout(planType, { upgradeConsent = null, upgradeQuoteId = null } = {}) {
        if (!Firebase.isSignedIn()) {
            alert(uiText('store.signInToPurchase', 'Please sign in to purchase.'));
            return;
        }
        const returnHint = createCheckoutReturnHint();

        try {
            document.body.style.cursor = 'wait';
            const idToken = await Firebase.getIdToken();

            if (!idToken) throw new Error(uiText('store.authTokenMissing', 'Could not retrieve authentication token.'));

            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    planType,
                    // Display language for the Stripe page and its consent
                    // text; the server canonicalises it and never treats it
                    // as legal evidence.
                    language: i18n?.getLanguage?.() || document.documentElement.lang || 'en',
                    // The consent version the player ticked in the upgrade
                    // dialog; only an in-place pass upgrade sends it.
                    ...(upgradeConsent ? { upgradeConsent } : {}),
                    ...(upgradeQuoteId ? { upgradeQuoteId } : {}),
                })
            });

            const data = await response.json();

            // The server is authoritative on region availability; this only
            // turns its refusal into an explanation instead of a dead button.
            if (response.status === 451) {
                alert(uiText(
                    'store.purchasesUnavailableInRegion',
                    'Purchases are not available in your region. You can still play and earn Stardust.',
                ));
                return;
            }

            /* A PLAN CHANGE IS NOT A CHECKOUT. An upgrade modifies the
               existing subscription, so it answers 200 with no `url` — which
               the branch below would have reported as "Failed to start
               checkout". The feature working would have looked exactly like
               it failing. */
            if (response.ok && data.upgraded === true) {
                billingStatusOverride = null;
                alert(uiText('store.upgradeDone', 'Your pass is upgraded. The extra Moonlight is already yours.'));
                // Re-read the verified entitlement; pending payments never
                // reach this success branch.
                renderPlans();
                return;
            }
            if (response.status === 409 && data.code === 'upgrade_quote_expired') {
                checkoutInflight.delete(planType);
                alert(data.refunded === true
                    ? uiText('store.upgradeRefunded', 'A full refund has been issued because your plan changed during payment. No upgrade or extra Moonlight was granted. Review your current plan before trying again.')
                    : uiText('store.upgradeQuoteExpired', 'The upgrade quote has changed or expired. Review a new quote before paying. Nothing was charged.'));
                renderPlans();
                return;
            }
            if (response.status === 409 && data.code === 'upgrade_interval_change_requires_portal') {
                alert(uiText('store.upgradeIntervalChangeInPortal', 'To change between monthly and yearly billing, use Manage Subscription. Nothing was charged.'));
                return;
            }
            /* Both of these carry an English sentence from the server, which
               must not be what a French player reads. Translated on the code. */
            if (response.status === 409 && data.code === 'plan_already_active') {
                billingStatusOverride = null;
                renderPlans();
                return;
            }
            if (response.status === 409 && data.code === 'moonlight_refill_subscription_required') {
                alert(uiText(
                    'moonlight.refillNeedsPass',
                    'A refill tops up a pass. Choose a pass below to begin one.',
                ));
                return;
            }
            /* The bar is no longer low — another tab's refill, a grant — so
               say when a refill opens, and re-read it so the button follows. */
            /* The refill is the Manga Pass's (2026-09-25); a store painted
               before that still shows the button to a Storyteller holder. */
            if (response.status === 409 && data.code === 'moonlight_refill_manga_pass_required') {
                alert(uiText('moonlight.refillMangaOnly', 'Refills are part of the Manga Pass.'));
                renderStoreMoonlight();
                return;
            }
            /* This billing period's refill is already bought (one per period,
               2026-09-28): say when refills open again, and re-read the bar so
               the button follows. */
            /* A payment in dispute puts purchases on hold until the bank
               decides (Terms 3.3): say so in the player's language. */
            if (response.status === 403 && data.code === 'payment_disputed') {
                alert(accountOnHoldCopy({ passOnHold: false }, uiText).purchasesLine);
                return;
            }
            // A bank reported a payment on this account as not its cardholder's.
            if (response.status === 403 && data.code === 'payment_flagged') {
                alert(accountOnHoldCopy({ passOnHold: false }, uiText).flaggedLine);
                return;
            }
            if (response.status === 409 && data.code === 'moonlight_refill_used_this_cycle') {
                alert(refillWhenLowText({ ...storeMoonlightStatus, ...data, refillUsedThisCycle: true }));
                renderStoreMoonlight();
                return;
            }
            if (response.status === 409 && data.code === 'moonlight_refill_reset_soon') {
                alert(uiText('moonlight.refillResetSoon', 'Moonlight resets shortly. Wait for the weekly reset before buying a refill.'));
                renderStoreMoonlight();
                return;
            }
            /* A refill page already paid in another tab is still being
               recorded: its Moonlight is on the way, and no second one opens. */
            if (response.status === 409 && data.code === 'moonlight_refill_processing') {
                alert(uiText(
                    'moonlight.refillProcessing',
                    'Your refill payment is being processed. The Moonlight arrives in a moment.',
                ));
                renderStoreMoonlight();
                return;
            }
            if (response.status === 409 && data.code === 'moonlight_refill_not_needed') {
                const whenLow = refillWhenLowText(data) || refillWhenLowText(storeMoonlightStatus);
                if (whenLow) alert(whenLow);
                renderStoreMoonlight();
                return;
            }
            /* A plan behind a live gift waits in a Stripe trial — deferred by the
               gift, or scheduled to start after it — and is not changed until
               the gift is over. Say so, and show the cards that already do. */
            if (response.status === 409 && (data.code === 'plan_deferred_by_gift' || data.code === 'plan_paused_by_gift' || data.code === 'plan_scheduled')) {
                alert(data.code !== 'plan_scheduled'
                    ? uiText('voucher.planDeferredByGift', 'Your next normal paid billing cycle starts when your gift ends. You can change the plan then; manage billing in the portal.')
                    : uiText('voucher.planScheduled', 'Your new pass is already set to start when your gift ends. Manage it in the billing portal.'));
                billingStatusOverride = null;
                renderPlans();
                return;
            }
            if (response.status === 409 && data.code === 'gift_ending_soon') {
                alert(uiText('voucher.giftEndingSoon', 'Wait until your gift ends to start the paid plan (less than two days remaining).'));
                return;
            }
            if (response.status === 409 && data.code === 'downgrade_requires_portal') {
                /* A FAST TRACK, not a signpost. Telling someone to "use Manage
                   Subscription" and leaving them to find it is the same
                   refusal with extra steps — so this explains what will happen
                   and then OPENS the billing portal, which is where the change
                   is actually made.

                   The change itself stays deferred to period end, deliberately:
                   a smaller pass takes effect when the one already paid for
                   runs out, so nothing bought is withdrawn. Scheduling it
                   in-app would need a Stripe subscription schedule, which is a
                   money-path build nobody has asked for. */
                alert(uiText(
                    'store.downgradeInPortal',
                    'Moving to a smaller pass takes effect at the end of your period. Use Manage Subscription.',
                ));
                await manageSubscription();
                return;
            }
            if (response.status === 409 && data.code === 'already_subscribed') {
                billingStatusOverride = data.billingState === 'subscribed'
                    ? 'subscribed'
                    : 'needs_management';
                repaintStore();
                return;
            }
            if (!response.ok) throw new Error(data.error || uiText('store.serverError', 'Server error'));

            if (data.url) {
                rememberCheckoutReturn(returnHint);
                window.location.href = data.url;
            } else {
                alert(uiText('store.checkoutNoUrl', 'Failed to start checkout: Received no URL'));
            }

        } catch (err) {
            console.error('[Store] Checkout Error:', err);
            alert(uiText('store.checkoutError', 'Checkout Error: {{message}}', { message: err.message }));
        } finally {
            document.body.style.cursor = 'default';
        }
    }

    async function manageSubscription() {
        if (!Firebase.isSignedIn()) {
            alert(uiText('store.signIn', 'Please sign in.'));
            return;
        }
        try {
            document.body.style.cursor = 'wait';
            const idToken = await Firebase.getIdToken();
            if (!idToken) throw new Error(uiText('store.authError', 'Auth error'));

            const response = await fetch('/api/create-portal-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });
            const data = await response.json();

            if (!response.ok) throw new Error(data.error || data.message || uiText('store.portalFailed', 'Failed to create portal session'));

            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error(uiText('store.portalNoUrl', 'No portal URL returned'));
            }
        } catch (err) {
            console.error('[Store] Portal Error:', err);
            alert(uiText('store.portalError', 'Error: {{message}}', { message: err.message }));
        } finally {
            document.body.style.cursor = 'default';
        }
    }

    return {
        init,
        open,
        close,
        isOpen: () => isOpen,
        checkout,
        beginCheckout,
        restoreCheckoutReturn,
        hasPendingCheckoutReturn,
        CHECKOUT_RETURN_HOLD_MS,
        manageSubscription,
        warmArt,
        warmData,
    };
})();

// Auto-init once parsing reaches the static Store markup. Direct initialization
// also makes reinjected test/dev bundles replace the previous singleton owner.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Store.init(), { once: true });
} else {
    Store.init();
}

export { Store };
